import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Palette, Users, Zap, Save, Tag, Plus, Trash2, Edit3, Mail, RotateCcw, Eye, Send, XCircle, X, Clock, ShieldAlert, ShieldCheck, Search, Loader2, FileText, FileSignature, MoreVertical, KeyRound, LogOut, PauseCircle, PlayCircle, History, UserCog, RefreshCw, Monitor, Download, Copy, Check, CreditCard, ExternalLink, Lock, CalendarDays, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FormDialog from '../components/FormDialog';
import CgvDeposees from '../components/parametres/CgvDeposees';
import { oauthRedirect } from '../utils/oauthRedirect';
import ConfirmDialog from '../components/ConfirmDialog';
import Toggle from '../components/Toggle';
import RichEditor from '../components/RichEditor';
import api from '../api/client';
import ChampHoraires from '../components/ChampHoraires';
import { toast } from '../hooks/use-toast';
import EmailMailboxSection from '../components/EmailMailboxSection';
import AuthPolicySection from '../components/settings/AuthPolicySection';
import IaIntegrationCard from '../components/settings/IaIntegrationCard';
import TeamSection from './_settings/TeamSection';
import TypesQuestionnaireSection from './_settings/TypesQuestionnaireSection';
import RichEmailEditor from '../components/email/RichEmailEditor';
import EmailLivePreview from '../components/email/EmailLivePreview';

const ROLE_STYLES = {
  'Administrateur': 'bg-[#EEF2FF] text-[#4F46E5]',
  'Formateur': 'bg-[#E0F2FE] text-[#0369A1]',
  'Commercial': 'bg-[#FEF3C7] text-[#92400E]',
  'Lecture seule': 'bg-[#F3F4F8] text-[#6B6C85]',
};

const TABS = [
  { id: 'org', label: 'Organisme', icon: Building2 },
  { id: 'team', label: 'Équipe', icon: Users },
  { id: 'categories', label: 'Catégories', icon: Tag },
  { id: 'docs_legal', label: 'Devis / Factures', icon: FileText },
  { id: 'branding', label: 'Personnalisation', icon: Palette },
  { id: 'qualiopi', label: 'Templates Qualiopi', icon: ShieldCheck },
  { id: 'docs_models', label: 'Modèles de documents', icon: FileText },
  { id: 'opcos', label: 'OPCO', icon: Building2 },
  { id: 'questionnaires', label: 'Questionnaires', icon: ClipboardList },
  { id: 'emails', label: 'Templates email', icon: Mail },
  { id: 'email_inbox', label: 'Boîte email', icon: Mail },
  { id: 'integrations', label: 'Intégrations', icon: Zap },
  { id: 'permissions', label: 'Rôles & permissions', icon: Lock },
  { id: 'auth_policy', label: 'Méthodes de connexion', icon: ShieldCheck },
  { id: 'subscription', label: 'Mon Abonnement', icon: CreditCard },
];

export default function Parametres() {
  const [tab, setTab] = useState('org');
  const { user } = useAuth();
  const [org, setOrg] = useState(null);

  useEffect(() => {
    api.get('/orgs/me').then(({ data }) => setOrg(data)).catch(() => {});
  }, []);

  const currentUser = {
    user_id: user?.user_id || null,
    name: user?.name || 'Utilisateur',
    email: user?.email || '',
    role: user?.role || 'Administrateur',
    org: org?.org_name || user?.org_name || user?.org || 'IFAAP',
    avatar: user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=7C5CFC&color=fff`,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
      <aside className="soft-card p-3 h-fit">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.id)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
            tab === t.id ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#4A4B63] hover:bg-[#F6F7FB]'
          }`}>
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </aside>

      <div className="space-y-4">
        {tab === 'org' && <OrgSection orgName={currentUser.org} onSaved={(n) => setOrg({ ...(org || {}), org_name: n })} />}
        {tab === 'team' && <TeamSection />}
        {tab === 'categories' && <CategoriesSection />}
        {tab === 'docs_legal' && <DocLegalSection />}
        {tab === 'branding' && <BrandingSection />}
        {tab === 'qualiopi' && <QualiopiTemplatesSection />}
        {tab === 'docs_models' && <DocsModelsSection />}
        {tab === 'opcos' && <OpcosSection />}
        {tab === 'questionnaires' && <TypesQuestionnaireSection />}
        {tab === 'emails' && <EmailTemplatesSection userEmail={currentUser.email} />}
        {tab === 'email_inbox' && <EmailMailboxSection currentUser={currentUser} restrictTo="org" />}
        {tab === 'integrations' && <IntegrationsSection />}
        {tab === 'permissions' && <RolePermissionsSection />}
        {tab === 'auth_policy' && <AuthPolicySection currentUser={currentUser} />}
        {tab === 'subscription' && <MonAbonnementSection />}
      </div>
    </div>
  );
}

function Field({ label, defaultValue, value, onChange, className = '', placeholder }) {
  return (
    <div className={className}>
      <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">{label}</label>
      <input
        {...(value !== undefined ? { value, onChange } : { defaultValue })}
        placeholder={placeholder}
        className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
      />
    </div>
  );
}

function OrgSection({ orgName, onSaved }) {
  const [name, setName] = useState(orgName);
  const [siret, setSiret] = useState('');
  const [declarationNumber, setDeclarationNumber] = useState('');
  const [qualiopiCert, setQualiopiCert] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [horairesDefaut, setHorairesDefaut] = useState([]);

  useEffect(() => setName(orgName), [orgName]);

  useEffect(() => {
    api.get('/orgs/me').then(({ data }) => {
      setSiret(data.siret || '');
      setDeclarationNumber(data.declaration_number || '');
      setQualiopiCert(data.qualiopi_cert || '');
      setContactEmail(data.contact_email || '');
      setPhone(data.phone || '');
      setAddress(data.address || '');
      setHorairesDefaut(Array.isArray(data.horaires_defaut) ? data.horaires_defaut : []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const handleSireneLookup = async () => {
    const q = (siret || '').replace(/\s+/g, '');
    if (!/^\d{9}$|^\d{14}$/.test(q)) {
      toast({ title: 'SIRET ou SIREN invalide', description: 'Saisissez 9 (SIREN) ou 14 chiffres (SIRET).' });
      return;
    }
    setLookingUp(true);
    try {
      const { data } = await api.get('/companies/sirene-lookup', { params: { q } });
      if (data.name) setName(data.name);
      if (data.siret) setSiret(data.siret);
      if (data.address) setAddress(data.address);
      toast({ title: 'Entreprise trouvée', description: `${data.name}${data.legal_form ? ' · ' + data.legal_form : ''}${data.is_active ? '' : ' · Cessée'}` });
    } catch (e) {
      const code = e?.response?.status;
      const msg = code === 404 ? 'Aucune entreprise trouvée'
                : code === 429 || code === 502 ? 'API publique saturée, réessayez dans un instant'
                : e?.response?.data?.detail || 'Erreur de recherche';
      toast({ title: 'Erreur', description: msg });
    } finally {
      setLookingUp(false);
    }
  };

  const save = async () => {
    try {
      const { data: resp } = await api.put('/orgs/me', {
        org_name: name,
        siret,
        declaration_number: declarationNumber,
        qualiopi_cert: qualiopiCert,
        contact_email: contactEmail,
        phone,
        address,
        horaires_defaut: horairesDefaut,
      });
      // Reflect persisted values from the server response (defensive confirmation)
      setSiret(resp.siret || '');
      setDeclarationNumber(resp.declaration_number || '');
      setQualiopiCert(resp.qualiopi_cert || '');
      setContactEmail(resp.contact_email || '');
      setPhone(resp.phone || '');
      setAddress(resp.address || '');
      setHorairesDefaut(Array.isArray(resp.horaires_defaut) ? resp.horaires_defaut : []);
      onSaved?.(resp.org_name || name);
      toast({ title: 'Enregistré', description: `Informations de l'organisme mises à jour` });
    } catch {
      toast({ title: 'Erreur', description: "Impossible d'enregistrer." });
    }
  };
  return (
    <div className="soft-card p-6">
      <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Informations de l'organisme</h3>
      <p className="text-[13px] text-[#8E8FA5] mb-5">Ces informations apparaissent sur vos documents officiels et sur le portail apprenant.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nom de l'organisme" value={name} onChange={(e) => setName(e.target.value)} />
        <div>
          <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">SIRET</label>
          <div className="flex gap-2">
            <input
              data-testid="org-siret-input"
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSireneLookup(); } }}
              placeholder="123 456 789 00012"
              className="flex-1 h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
            />
            <button
              data-testid="org-sirene-lookup"
              type="button"
              onClick={handleSireneLookup}
              disabled={lookingUp || !siret}
              title="Rechercher sur le registre SIRENE (recherche-entreprises.api.gouv.fr)"
              className="h-10 px-3 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              SIRENE
            </button>
          </div>
          <p className="text-[11px] text-[#8E8FA5] mt-1">Saisissez 9 ou 14 chiffres puis cliquez SIRENE pour auto-remplir le nom et l'adresse depuis le registre public.</p>
        </div>
        <Field label="N° déclaration d'activité" value={declarationNumber} onChange={(e) => setDeclarationNumber(e.target.value)} placeholder="11 75 ..." />
        <Field label="Certificat Qualiopi n°" value={qualiopiCert} onChange={(e) => setQualiopiCert(e.target.value)} placeholder="QP-2024-..." />
        <Field label="Email de contact" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="contact@ifaap.fr" />
        <Field label="Téléphone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="01 23 45 67 89" />
        <Field label="Adresse" value={address} onChange={(e) => setAddress(e.target.value)} className="md:col-span-2" placeholder="N° rue, ville, code postal" />
        <div className="md:col-span-2">
          <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Horaire type d’une journée de formation</label>
          <ChampHoraires
            testId="org-horaires-defaut"
            value={horairesDefaut}
            onChange={setHorairesDefaut}
            aide="Il se propose sur chaque nouvelle formation, où il reste modifiable. Le changer ici ne touche aucune formation déjà créée — ni aucune convention déjà signée."
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <button data-testid="settings-save-org" onClick={save} disabled={!loaded} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95 disabled:opacity-50">
          <Save className="w-4 h-4" /> Enregistrer
        </button>
      </div>
    </div>
  );
}

// Les quatre categories d'actions de l'article L.6313-1, telles que la charte
// d'usage Qualiopi les nomme. Ce ne sont pas des libelles de la maison : ils ne
// se modifient pas, et une cinquieme n'existe pas. Le serveur tient la meme
// liste (`routes/qualiopi_marque.py`) et ferme la porte : un code qui n'est pas
// l'une des quatre n'entre pas en base.
const CATEGORIES_QUALIOPI = [
  ['FORMATION', 'actions de formation'],
  ['BILAN', 'bilans de compétences'],
  ['VAE', "actions permettant de faire valider les acquis de l'expérience"],
  ['APPRENTISSAGE', 'actions de formation par apprentissage'],
];

function DocLegalSection() {
  const [loaded, setLoaded] = useState(false);
  const [f, setF] = useState({
    legal_form: '', rcs_city: '', rcs_number: '',
    tva_intra: '', ape_code: '',
    capital_social: 0,
    iban: '', bic: '', bank_name: '',
    modes_reglement: ['VIREMENT'],
    mode_reglement_defaut: 'VIREMENT',
    payment_terms_days: 30,
    quote_validity_days: 30,
    cgv_joindre_au_devis: true,
    late_penalty_rate: "Trois fois le taux d'intérêt légal",
    recovery_fee_eur: 40,
    no_discount_clause: true,
    reserve_of_title: false,
    auto_send_on_validate: true,
    qualiopi_scope: 'Actions de formation',
    qualiopi_categories: [],
    declaration_region: '',
    qualiopi_portee: 'PARTOUT',
    custom_legal_footer: '',
  });

  // Les modes viennent du serveur : deux listes pour un seul vocabulaire
  // finiraient par ne pas dire la meme chose, et c'est la facture qui le
  // paierait.
  const [modesReglement, setModesReglement] = useState([]);
  // Stripe branche veut dire que le client **peut** payer par carte : l'ecran
  // le dit, il ne coche pas a la place de qui regle. Une mention sur une
  // facture est un engagement, et elle n'a pas a apparaitre parce qu'un
  // connecteur a ete branche.
  const [stripeActif, setStripeActif] = useState(null);

  useEffect(() => {
    api.get('/invoices/reglement/modes')
      .then((r) => setModesReglement(r.data || []))
      .catch(() => setModesReglement([]));
    api.get('/integrations/stripe')
      .then((r) => {
        const d = r.data || {};
        setStripeActif(!!(d[d.mode] || {}).configured);
      })
      .catch(() => setStripeActif(null));
  }, []);

  const reload = async () => {
    const { data } = await api.get('/orgs/me');
    setF({
      legal_form: data.legal_form || '',
      rcs_city: data.rcs_city || '',
      rcs_number: data.rcs_number || '',
      tva_intra: data.tva_intra || '',
      ape_code: data.ape_code || '',
      capital_social: Number(data.capital_social) || 0,
      iban: data.iban || '',
      bic: data.bic || '',
      bank_name: data.bank_name || '',
      modes_reglement: (Array.isArray(data.modes_reglement) && data.modes_reglement.length)
        ? data.modes_reglement : ['VIREMENT'],
      mode_reglement_defaut: data.mode_reglement_defaut || 'VIREMENT',
      payment_terms_days: Number.isFinite(Number(data.payment_terms_days)) ? Number(data.payment_terms_days) : 30,
      quote_validity_days: Number.isFinite(Number(data.quote_validity_days)) ? Number(data.quote_validity_days) : 30,
      cgv_joindre_au_devis: data.cgv_joindre_au_devis !== false,
      late_penalty_rate: data.late_penalty_rate || "Trois fois le taux d'intérêt légal",
      recovery_fee_eur: Number(data.recovery_fee_eur) || 40,
      no_discount_clause: data.no_discount_clause !== false,
      reserve_of_title: !!data.reserve_of_title,
      auto_send_on_validate: data.auto_send_on_validate !== false,
      qualiopi_scope: data.qualiopi_scope || 'Actions de formation',
      qualiopi_categories: data.qualiopi_categories || [],
      declaration_region: data.declaration_region || '',
      qualiopi_portee: data.qualiopi_portee || 'PARTOUT',
      custom_legal_footer: data.custom_legal_footer || '',
    });
    return data;
  };

  useEffect(() => {
    reload().finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    try {
      // org_name is required by backend validator — read current from org_profiles context
      const { data: current } = await api.get('/orgs/me');
      await api.put('/orgs/me', { org_name: current.org_name, ...f });
      // Force reload so the UI reflects the exact persisted values (defensive — confirms save worked)
      await reload();
      toast({ title: 'Enregistré', description: 'Mentions légales Devis / Factures mises à jour — elles apparaîtront sur vos prochains PDFs.' });
    } catch {
      toast({ title: 'Erreur', description: "Impossible d'enregistrer." });
    }
  };

  const [lookingUpSiret, setLookingUpSiret] = useState(false);
  const fillFromSirene = async () => {
    try {
      const { data: current } = await api.get('/orgs/me');
      const q = (current.siret || '').replace(/\s+/g, '');
      if (!/^\d{9}$|^\d{14}$/.test(q)) {
        toast({ title: 'SIRET manquant ou invalide', description: "Renseigne d'abord le SIRET dans l'onglet Organisme (9 ou 14 chiffres)." });
        return;
      }
      setLookingUpSiret(true);
      const { data } = await api.get('/companies/sirene-lookup', { params: { q } });
      // Helper: parse address to extract city (for rcs_city default)
      const city = (() => {
        const a = (data.address || '').split(',').pop() || '';
        return a.replace(/\d{5}/g, '').trim();
      })();
      const next = { ...f };
      if (data.legal_form) next.legal_form = data.legal_form;
      if (data.capital_social && Number(data.capital_social) > 0) next.capital_social = Number(data.capital_social);
      if (data.tva_intra) next.tva_intra = data.tva_intra;
      if (data.naf_code) next.ape_code = data.naf_code;
      if (city && !f.rcs_city) next.rcs_city = city;
      if (data.siren && !f.rcs_number) next.rcs_number = `B ${data.siren.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`.trim();
      setF(next);
      // Also save org_name + address from SIRENE into org_profiles (if changed)
      const putBody = { org_name: data.name || current.org_name, ...next };
      if (data.address && !current.address) putBody.address = data.address;
      await api.put('/orgs/me', putBody);
      await reload();
      toast({ title: 'Champs remplis depuis SIRENE', description: `${data.name}${data.legal_form ? ' · ' + data.legal_form : ''}. N'oublie pas de vérifier et Enregistrer.` });
    } catch (e) {
      const code = e?.response?.status;
      const msg = code === 404 ? 'Aucune entreprise trouvée'
                : code === 429 || code === 502 ? 'API publique saturée, réessayez dans un instant'
                : e?.response?.data?.detail || 'Erreur de recherche';
      toast({ title: 'Erreur SIRENE', description: msg });
    } finally {
      setLookingUpSiret(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="soft-card p-6">
        <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mentions légales — Devis & Factures</h3>
        <p className="text-[13px] text-[#8E8FA5] mb-5">Ces informations apparaissent automatiquement dans le pied de page de vos devis et factures PDF, conformément au Code de commerce (art. R123-237, L441-10) et aux obligations des organismes de formation.</p>

        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide">Identité juridique</h4>
          <button
            data-testid="legal-sirene-fill"
            type="button"
            onClick={fillFromSirene}
            disabled={lookingUpSiret}
            title="Récupérer les informations légales depuis le registre SIRENE (via le SIRET saisi dans l'onglet Organisme)"
            className="h-9 px-3 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {lookingUpSiret ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Remplir depuis SIRENE
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Field label="Forme juridique" value={f.legal_form} onChange={(e) => upd('legal_form', e.target.value)} placeholder="SAS, SARL, SASU, Association..." />
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Capital social (€)</label>
            <input type="number" step="1" min="0" value={f.capital_social || ''} onChange={(e) => upd('capital_social', Number(e.target.value) || 0)} data-testid="legal-capital" placeholder="10000" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none" />
          </div>
          <Field label="Ville du RCS" value={f.rcs_city} onChange={(e) => upd('rcs_city', e.target.value)} placeholder="Paris" />
          <Field label="N° RCS" value={f.rcs_number} onChange={(e) => upd('rcs_number', e.target.value)} placeholder="B 123 456 789" />
          <Field label="TVA intracommunautaire" value={f.tva_intra} onChange={(e) => upd('tva_intra', e.target.value)} placeholder="FR 12 345678901" />
          <Field label="Code APE / NAF" value={f.ape_code} onChange={(e) => upd('ape_code', e.target.value)} placeholder="8559A" />
        </div>

        <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide mb-3">Coordonnées bancaires</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Field label="Nom de la banque" value={f.bank_name} onChange={(e) => upd('bank_name', e.target.value)} placeholder="Crédit Mutuel" />
          <Field label="BIC / SWIFT" value={f.bic} onChange={(e) => upd('bic', e.target.value)} placeholder="CMCIFRPP" />
          <Field label="IBAN" value={f.iban} onChange={(e) => upd('iban', e.target.value)} className="md:col-span-2" placeholder="FR76 3000 4000 1234 5678 9000 123" />
        </div>

        <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide mb-3">Conditions de paiement</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="md:col-span-2">
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Modes de règlement acceptés</label>
            <div className="flex flex-wrap gap-4">
              {(modesReglement.length ? modesReglement : [{ code: 'VIREMENT', libelle: 'virement bancaire' }]).map((m) => {
                const coche = (f.modes_reglement || []).includes(m.code);
                return (
                  <label key={m.code} className="flex items-center gap-2 text-[13px] text-[#4A4B63]">
                    <input
                      type="checkbox"
                      checked={coche}
                      data-testid={`legal-mode-${m.code}`}
                      onChange={() => {
                        const avant = f.modes_reglement || [];
                        const apres = coche ? avant.filter((c) => c !== m.code) : [...avant, m.code];
                        // Jamais aucun mode : la mention est obligatoire sur
                        // une facture, et le serveur retomberait sur le
                        // virement sans le dire. On decoche l'autre, pas le
                        // dernier.
                        upd('modes_reglement', apres.length ? apres : avant);
                      }}
                      className="w-4 h-4 rounded border-[#D6D3FF] accent-[#4F46E5]"
                    />
                    {m.libelle.charAt(0).toUpperCase() + m.libelle.slice(1)}
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-[#8E8FA5] mt-1">L'IBAN n'est imprimé que si le virement ou le prélèvement est accepté.</p>
            {stripeActif === true && !(f.modes_reglement || []).includes('CARTE') && (
              <p className="text-[11px] text-[#4F46E5] mt-1" data-testid="stripe-propose-carte">
                Stripe est connecté : vos clients peuvent <strong>déjà</strong> payer par carte, et vous leur envoyez des liens de paiement. Cochez « Carte bancaire » pour que la facture le dise.
              </p>
            )}
            {stripeActif === false && (f.modes_reglement || []).includes('CARTE') && (
              <p className="text-[11px] text-[#B45309] mt-1" data-testid="stripe-manquant-carte">
                La carte est annoncée sur vos factures, mais <strong>Stripe n'est pas connecté</strong> : vos clients n'ont aucun moyen de payer par carte.
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Mode par défaut</label>
            <select
              value={f.mode_reglement_defaut || 'VIREMENT'}
              onChange={(e) => upd('mode_reglement_defaut', e.target.value)}
              data-testid="legal-mode-defaut"
              className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none"
            >
              {(modesReglement.filter((m) => (f.modes_reglement || []).includes(m.code)))
                .map((m) => (
                  <option key={m.code} value={m.code}>{m.libelle.charAt(0).toUpperCase() + m.libelle.slice(1)}</option>
                ))}
            </select>
            <p className="text-[11px] text-[#8E8FA5] mt-1">
              Il passe en tête de la phrase imprimée : « Règlement par {(modesReglement.find((m) => m.code === (f.mode_reglement_defaut || 'VIREMENT')) || {}).libelle || 'virement bancaire'} … le 27/10/2026 », et c'est le seul qui porte un <strong>code de facturation électronique</strong> — la norme n'en attend qu'un.
              {(() => {
                const d = modesReglement.find((m) => m.code === (f.mode_reglement_defaut || 'VIREMENT'));
                if (!d) return null;
                return d.code_electronique
                  ? <> Code {d.code_electronique} (UNTDID 4461).</>
                  : <span className="text-[#B45309]"> Code électronique à confirmer pour ce mode : la facture électronique n'en portera aucun.</span>;
              })()}
            </p>
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Délai de paiement (jours)</label>
            <input type="number" min="0" step="1" value={f.payment_terms_days ?? ''} onChange={(e) => upd('payment_terms_days', e.target.value === '' ? 0 : Math.max(0, Number(e.target.value)))} data-testid="legal-terms-days" placeholder="30" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none" />
            <p className="text-[11px] text-[#8E8FA5] mt-1">
              <strong>0 = paiement à réception de facture.</strong> Délai maximum : 60 jours calendaires (art. L441-10 CdC).
            </p>
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Indemnité forfaitaire de recouvrement (€)</label>
            <input type="number" min="0" step="1" value={f.recovery_fee_eur || ''} onChange={(e) => upd('recovery_fee_eur', Number(e.target.value) || 0)} data-testid="legal-recovery-fee" placeholder="40" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none" />
            <p className="text-[11px] text-[#8E8FA5] mt-1">40 € minimum imposé par la loi (décret 2012-1115).</p>
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Validité d'un devis (jours)</label>
            <input type="number" min="0" step="1" value={f.quote_validity_days ?? ''} onChange={(e) => upd('quote_validity_days', e.target.value === '' ? 0 : Math.max(0, Number(e.target.value)))} data-testid="legal-quote-validity" placeholder="30" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none" />
            <p className="text-[11px] text-[#8E8FA5] mt-1">Compté depuis la date du devis. C'est elle qui s'imprime en « Valide jusqu'au ».</p>
          </div>
          <Field label="Taux des pénalités de retard" value={f.late_penalty_rate} onChange={(e) => upd('late_penalty_rate', e.target.value)} className="md:col-span-2" placeholder="Trois fois le taux d'intérêt légal" />
        </div>

        <CgvDeposees
          joindreAuDevis={f.cgv_joindre_au_devis}
          onJoindreAuDevis={(v) => upd('cgv_joindre_au_devis', v)}
        />

        <div className="space-y-2 mb-6">
          <label className="flex items-center gap-2 text-[13px] text-[#4A4B63] cursor-pointer">
            <input type="checkbox" checked={f.no_discount_clause} onChange={(e) => upd('no_discount_clause', e.target.checked)} className="w-4 h-4 rounded text-[#4F46E5]" />
            <span>Afficher <em>"Pas d'escompte pour paiement anticipé"</em> sur les factures</span>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-[#4A4B63] cursor-pointer">
            <input type="checkbox" checked={f.reserve_of_title} onChange={(e) => upd('reserve_of_title', e.target.checked)} className="w-4 h-4 rounded text-[#4F46E5]" />
            <span>Afficher la <strong>clause de réserve de propriété</strong></span>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-[#4A4B63] cursor-pointer" data-testid="org-auto-send-toggle">
            <input type="checkbox" checked={f.auto_send_on_validate} onChange={(e) => upd('auto_send_on_validate', e.target.checked)} className="w-4 h-4 rounded text-[#4F46E5]" />
            <span><strong>Envoyer automatiquement par email à la validation</strong> de la facture (sinon envoi manuel depuis la fiche facture).</span>
          </label>
        </div>

        <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide mb-3">Déclaration d'activité</h4>
        <div className="mb-6">
          <Field label="Région du Préfet" value={f.declaration_region} onChange={(e) => upd('declaration_region', e.target.value)} placeholder="Nouvelle-Aquitaine" />
          <p className="text-[11px] text-[#8E8FA5] mt-1">
            Le pied écrit : <em>« Déclaration d'activité enregistrée sous le n° … auprès du Préfet de région {f.declaration_region || '…'}. Cet enregistrement ne vaut pas agrément de l'État. »</em>
            {' '}Cette dernière phrase est obligatoire (art. L.6352-12) et porte sur la déclaration — pas sur Qualiopi.
          </p>
        </div>

        <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide mb-3">Mention Qualiopi</h4>
        <div className="mb-6 space-y-3">
          <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3">
            <div className="text-[12px] font-semibold text-[#4A4B63] mb-2">Catégories réellement certifiées</div>
            {CATEGORIES_QUALIOPI.map(([code, libelle]) => (
              <label key={code} className="flex items-start gap-2 text-[13px] text-[#4A4B63] cursor-pointer py-0.5" data-testid={`qualiopi-cat-${code}`}>
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded text-[#4F46E5] mt-0.5"
                  checked={(f.qualiopi_categories || []).includes(code)}
                  onChange={(e) => {
                    const dejà = f.qualiopi_categories || [];
                    upd('qualiopi_categories', e.target.checked
                      ? [...dejà, code] : dejà.filter((c) => c !== code));
                  }}
                />
                <span>{libelle}</span>
              </label>
            ))}
            <p className="text-[11px] text-[#92400E] mt-2">
              Ne cochez que ce qui figure sur votre certificat : une catégorie non certifiée qui apparaît sur un document est un manquement, et le certificateur demande une mise en conformité sous 30 jours.
            </p>
          </div>

          <div className="rounded-xl border border-[#EFF0F6] p-3">
            <div className="text-[12px] font-semibold text-[#4A4B63] mb-1">Ce que le pied écrira</div>
            <div className="text-[12px] text-[#1A1B2E] italic leading-snug" data-testid="qualiopi-apercu">
              {(f.qualiopi_categories || []).length === 0
                ? <span className="text-[#B45309] not-italic font-semibold">Aucune catégorie cochée — la mention ne s'imprimera pas. Une phrase sans catégorie est non conforme, nous préférons ne rien écrire.</span>
                : `« La certification qualité a été délivrée au titre de la ou des catégories d'actions suivantes : ${CATEGORIES_QUALIOPI.filter(([c]) => (f.qualiopi_categories || []).includes(c)).map(([, l]) => l).join(', ').replace(/, ([^,]*)$/, ' et $1')} »`}
            </div>
            <p className="text-[11px] text-[#8E8FA5] mt-2">
              C'est la formulation de la charte d'usage, mot pour mot : elle garde « la ou des » même pour une seule catégorie, et le numéro de certificat n'en fait pas partie.
            </p>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-[#4A4B63] mb-1">Où la mention peut figurer</label>
            <select
              data-testid="qualiopi-portee"
              value={f.qualiopi_portee}
              onChange={(e) => upd('qualiopi_portee', e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-[#E6E7F0] text-[13px] outline-none focus:border-[#4F46E5]"
            >
              <option value="PARTOUT">Sur tous les documents</option>
              <option value="GENERAUX">Seulement les documents généraux (ni convocation, ni convention, ni facture, ni fiche programme)</option>
              <option value="NULLE_PART">Nulle part</option>
            </select>
            <p className="text-[11px] text-[#8E8FA5] mt-1">
              La marque sert à une information générale sur l'organisme. Plusieurs sources la disent interdite sur un document qui concerne une action de formation précise — <strong>le règlement d'usage remis par votre certificateur est le seul texte qui tranche</strong>. Ce réglage est là pour obéir sans toucher au code.
            </p>
          </div>
        </div>

        <h4 className="text-[13px] font-bold text-[#4A4B63] uppercase tracking-wide mb-3">Texte libre personnalisé (avancé)</h4>
        <textarea
          data-testid="legal-custom-footer"
          value={f.custom_legal_footer}
          onChange={(e) => upd('custom_legal_footer', e.target.value)}
          rows={4}
          placeholder="Laissez vide pour utiliser la génération automatique ci-dessus. Si rempli, ce texte remplace intégralement le pied de page légal des PDFs (3 lignes max, sautez une ligne pour séparer)."
          className="w-full px-3.5 py-2.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none resize-none"
        />
        <p className="text-[11px] text-[#8E8FA5] mt-1">Utilisé uniquement si tu veux contrôler manuellement chaque ligne. Sinon les champs ci-dessus sont concaténés automatiquement.</p>

        <div className="mt-6 flex justify-end">
          <button data-testid="settings-save-docs-legal" onClick={save} disabled={!loaded} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95 disabled:opacity-50">
            <Save className="w-4 h-4" /> Enregistrer
          </button>
        </div>
      </div>

      <div className="soft-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Prévisualiser le rendu complet</h3>
            <p className="text-[12px] text-[#8E8FA5] mt-0.5">Générez un PDF d'exemple (facture démo) avec toutes vos mentions légales actuelles pour voir le rendu final imprimé.</p>
          </div>
          <button
            data-testid="legal-preview-pdf"
            onClick={async () => {
              try {
                // Save current form silently first so the preview uses the latest values
                const { data: current } = await api.get('/orgs/me');
                await api.put('/orgs/me', { org_name: current.org_name, ...f });
                const res = await api.get('/orgs/branding/preview.pdf', { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
                toast({ title: 'Aperçu généré', description: 'Le PDF s\'ouvre dans un nouvel onglet.' });
              } catch {
                toast({ title: 'Erreur', description: 'Impossible de générer l\'aperçu.' });
              }
            }}
            className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#EEF2FF] whitespace-nowrap"
          >
            <Eye className="w-4 h-4" /> Prévisualiser un PDF
          </button>
        </div>
        <h3 className="text-[15px] font-semibold text-[#1A1B2E] mt-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Aperçu du pied de page</h3>
        <p className="text-[12px] text-[#8E8FA5] mb-3">Voici à quoi ressemblera le footer de vos PDFs (3 lignes max) :</p>
        <DocLegalFooterPreview f={f} />
      </div>
    </div>
  );
}

function DocLegalFooterPreview({ f }) {
  const [orgName, setOrgName] = useState('IFAAP');
  const [orgProfile, setOrgProfile] = useState({});
  const reloadOrg = async () => {
    try {
      const { data } = await api.get('/orgs/me');
      setOrgName(data.org_name || 'IFAAP');
      setOrgProfile({
        siret: data.siret, declaration_number: data.declaration_number,
        qualiopi_cert: data.qualiopi_cert, address: data.address,
        contact_email: data.contact_email, phone: data.phone,
      });
    } catch (e) { console.warn("[silent]", e); }
  };
  useEffect(() => {
    reloadOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Re-fetch when f changes (after save → orgProfile might have been updated via SIRENE lookup)
  useEffect(() => {
    reloadOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.legal_form, f.capital_social]);
  const custom = (f.custom_legal_footer || '').trim();
  let lines;
  if (custom) {
    lines = custom.split('\n').filter(Boolean).slice(0, 3);
  } else {
    const l1 = [orgName];
    if (f.legal_form) l1.push(f.legal_form);
    if (orgProfile.siret) l1.push(`SIRET ${orgProfile.siret}`);
    if (Number(f.capital_social) > 0) l1.push(`Capital social : ${Number(f.capital_social).toLocaleString('fr-FR')} €`);
    if (f.rcs_city || f.rcs_number) l1.push(`RCS ${f.rcs_city || ''} ${f.rcs_number || ''}`.trim());
    if (f.ape_code) l1.push(`APE ${f.ape_code}`);
    if (f.tva_intra) l1.push(`TVA ${f.tva_intra}`);
    const l2parts = [];
    if (orgProfile.declaration_number) l2parts.push(`Déclaration d'activité enregistrée sous le n° ${orgProfile.declaration_number} auprès du Préfet de région`);
    if (orgProfile.qualiopi_cert) l2parts.push(`Certifié Qualiopi n° ${orgProfile.qualiopi_cert} — ${f.qualiopi_scope || 'Actions de formation'}`);
    let l2 = l2parts.join(' · ');
    if (l2) l2 += " · Cet enregistrement ne vaut pas agrément de l'État";
    const l3parts = [];
    if (orgProfile.address) l3parts.push(orgProfile.address);
    if (orgProfile.contact_email) l3parts.push(orgProfile.contact_email);
    if (orgProfile.phone) l3parts.push(`Tél. ${orgProfile.phone}`);
    const l3 = l3parts.join(' · ');
    lines = [l1.join(' · '), l2, l3].filter(Boolean);
  }
  // Approximate character limit for a printed A4 line at 7pt font (center-aligned between margins).
  // A4 width ~174mm usable (210 - 18*2). At 7pt Helvetica, ~2.0 char/mm → ~135 chars safely.
  // The PDF now word-wraps long lines but we show the approximate wrapped preview here.
  return (
    <div className="p-4 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] font-mono text-[10.5px] text-[#6B6C85] min-h-[60px] overflow-hidden">
      {lines.length === 0 ? (
        <div className="text-center"><em>— Aucune mention renseignée —</em></div>
      ) : (
        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={i} className="text-center leading-snug break-words whitespace-normal" style={{ wordBreak: 'break-word' }}>
              {l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CategoriesSection() {
  const [cats, setCats] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/categories').then(({ data }) => setCats(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const PALETTE = [
    { value: '#4F46E5', label: 'Violet (principal)' },
    { value: '#14B8A6', label: 'Teal (secondaire)' },
    { value: '#3B82F6', label: 'Bleu' },
    { value: '#10B981', label: 'Vert' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#EF4444', label: 'Rouge' },
    { value: '#EC4899', label: 'Rose' },
    { value: '#8B5CF6', label: 'Violet clair' },
    { value: '#06B6D4', label: 'Cyan' },
    { value: '#64748B', label: 'Ardoise' },
  ];

  const FIELDS = [
    { name: 'name', label: 'Nom de la catégorie', required: true, full: true },
    { name: 'description', label: 'Description', full: true },
    { name: 'color', label: 'Couleur', type: 'color-palette', options: PALETTE, full: true, defaultValue: '#4F46E5' },
  ];

  const handleCreate = async (values) => {
    const { data } = await api.post('/categories', { ...values, color: values.color || '#4F46E5' });
    setCats((prev) => [...prev, data]);
    toast({ title: 'Catégorie créée', description: data.name });
  };

  const handleUpdate = async (values) => {
    const { data } = await api.put(`/categories/${editing.id}`, values);
    setCats((prev) => prev.map((c) => c.id === data.id ? data : c));
    toast({ title: 'Catégorie modifiée', description: data.name });
  };

  const handleDelete = async () => {
    await api.delete(`/categories/${deleting.id}`);
    setCats((prev) => prev.filter((c) => c.id !== deleting.id));
    toast({ title: 'Catégorie supprimée' });
  };

  return (
    <div className="soft-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Catégories de formation</h3>
          <p className="text-[13px] text-[#8E8FA5]">{cats.length} catégorie(s) disponible(s)</p>
        </div>
        <button data-testid="category-create-btn" onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {loading && <div className="text-[13px] text-[#8E8FA5]">Chargement…</div>}
      {!loading && cats.length === 0 && (
        <div className="text-[13px] text-[#8E8FA5] py-8 text-center">Aucune catégorie. Créez votre première catégorie pour organiser vos programmes.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cats.map((c) => (
          <div key={c.id} data-testid={`category-${c.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#F3F4F8] hover:bg-[#FAFAFE] transition-colors">
            <span className="w-4 h-4 rounded-md shrink-0" style={{ background: c.color || '#4F46E5' }} />
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1A1B2E] truncate">{c.name}</div>
              {c.description && <div className="text-[12px] text-[#8E8FA5] truncate">{c.description}</div>}
            </div>
            <button data-testid={`category-edit-${c.id}`} onClick={() => setEditing(c)} className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center" title="Modifier"><Edit3 className="w-3.5 h-3.5" /></button>
            <button data-testid={`category-delete-${c.id}`} onClick={() => setDeleting(c)} className="w-8 h-8 rounded-lg hover:bg-[#FEF2F2] text-[#B91C1C] flex items-center justify-center" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nouvelle catégorie" fields={FIELDS} onSubmit={handleCreate} />
      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Modifier la catégorie" fields={FIELDS} initialValues={editing} onSubmit={handleUpdate} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer cette catégorie ?" description={deleting?.name} onConfirm={handleDelete} />
    </div>
  );
}

// TeamSection has been extracted to ./_settings/TeamSection.jsx (Vague 99 — admin team actions).

function BrandingSection() {
  const [primary, setPrimary] = useState('#4F46E5');
  const [secondary, setSecondary] = useState('#14B8A6');
  const [accent, setAccent] = useState('#F59E0B');
  const [textColor, setTextColor] = useState('#1A1B2E');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [logoB64, setLogoB64] = useState(null);
  const [logoPrev, setLogoPrev] = useState(null);
  const [backgroundB64, setBackgroundB64] = useState(null);
  const [backgroundPrev, setBackgroundPrev] = useState(null);
  const [showBackground, setShowBackground] = useState(true);
  const [watermark, setWatermark] = useState('');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.08);
  const [watermarkSize, setWatermarkSize] = useState(72);
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [showHeader, setShowHeader] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [marginTop, setMarginTop] = useState(14);
  const [marginBottom, setMarginBottom] = useState(18);
  const [marginLeft, setMarginLeft] = useState(18);
  const [marginRight, setMarginRight] = useState(18);
  const [headerHeight, setHeaderHeight] = useState(14);
  const [footerHeight, setFooterHeight] = useState(12);
  const [headerBgEnabled, setHeaderBgEnabled] = useState(true);
  const [headerBgColor, setHeaderBgColor] = useState('#4F46E5');
  const [headerTextColor, setHeaderTextColor] = useState('#FFFFFF');
  const [footerBgEnabled, setFooterBgEnabled] = useState(false);
  const [footerBgColor, setFooterBgColor] = useState('#4F46E5');
  const [footerTextColor, setFooterTextColor] = useState('#8E8FA5');
  const [backgroundMode, setBackgroundMode] = useState('full_page');
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewKind, setPreviewKind] = useState('invoice');

  useEffect(() => {
    api.get('/orgs/branding').then(({ data }) => {
      setPrimary(data.primary || '#4F46E5');
      setSecondary(data.secondary || '#14B8A6');
      setAccent(data.accent || '#F59E0B');
      setTextColor(data.text_color || '#1A1B2E');
      setBgColor(data.bg_color || '#FFFFFF');
      setLogoB64(data.logo_b64 || null);
      setLogoPrev(data.logo_b64 || null);
      setBackgroundB64(data.background_b64 || null);
      setBackgroundPrev(data.background_b64 || null);
      setShowBackground(data.show_background !== false);
      setWatermark(data.watermark_text || '');
      setWatermarkOpacity(data.watermark_opacity ?? 0.08);
      setWatermarkSize(data.watermark_size ?? 72);
      setHeaderText(data.header_text || '');
      setFooterText(data.footer_text || '');
      setShowHeader(data.show_header !== false);
      setShowFooter(data.show_footer !== false);
      setShowLogo(data.show_logo !== false);
      setMarginTop(data.margin_top_mm ?? 14);
      setMarginBottom(data.margin_bottom_mm ?? 18);
      setMarginLeft(data.margin_left_mm ?? 18);
      setMarginRight(data.margin_right_mm ?? 18);
      setHeaderHeight(data.header_height_mm ?? 14);
      setFooterHeight(data.footer_height_mm ?? 12);
      setHeaderBgEnabled(data.header_bg_enabled !== false);
      setHeaderBgColor(data.header_bg_color || data.primary || '#4F46E5');
      setHeaderTextColor(data.header_text_color || '#FFFFFF');
      setFooterBgEnabled(!!data.footer_bg_enabled);
      setFooterBgColor(data.footer_bg_color || data.primary || '#4F46E5');
      setFooterTextColor(data.footer_text_color || '#8E8FA5');
      setBackgroundMode(data.background_mode || 'full_page');
    }).catch(() => {});
  }, []);

  const readFile = (file, setB64, setPrev, maxKb = 400) => {
    if (!file) return;
    if (file.size > maxKb * 1024) { toast({ title: `Fichier trop gros (max ${maxKb} Ko)` }); return; }
    const reader = new FileReader();
    reader.onload = () => { setB64(reader.result); setPrev(reader.result); };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/orgs/branding', {
        primary, secondary, accent, text_color: textColor, bg_color: bgColor,
        logo_b64: logoB64, background_b64: backgroundB64, show_background: showBackground,
        watermark_text: watermark, watermark_opacity: watermarkOpacity, watermark_size: watermarkSize,
        header_text: headerText, footer_text: footerText,
        show_header: showHeader, show_footer: showFooter, show_logo: showLogo,
        margin_top_mm: Number(marginTop) || 14,
        margin_bottom_mm: Number(marginBottom) || 18,
        margin_left_mm: Number(marginLeft) || 18,
        margin_right_mm: Number(marginRight) || 18,
        header_height_mm: Number(headerHeight) || 14,
        footer_height_mm: Number(footerHeight) || 12,
        header_bg_enabled: headerBgEnabled, header_bg_color: headerBgColor, header_text_color: headerTextColor,
        footer_bg_enabled: footerBgEnabled, footer_bg_color: footerBgColor, footer_text_color: footerTextColor,
        background_mode: backgroundMode,
      });
      toast({ title: 'Charte enregistrée', description: 'Appliquée sur tous vos prochains documents PDF.' });
    } catch {
      toast({ title: 'Erreur' });
    } finally {
      setSaving(false);
    }
  };

  const visualize = async () => {
    setPreviewing(true);
    try {
      // 1) persist the current state so the PDF reflects exactly what the user sees
      await save();
      // 2) download the preview PDF and open it in a new tab (with blob URL — fallback to download)
      const res = await api.get('/orgs/branding/preview.pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const href = URL.createObjectURL(blob);
      const win = window.open(href, '_blank');
      if (!win) {
        // Popup blocked: fall back to direct download
        const a = document.createElement('a');
        a.href = href; a.download = 'apercu-charte.pdf';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      // Revoke after a short delay (browser needs the URL while rendering)
      setTimeout(() => URL.revokeObjectURL(href), 60_000);
      toast({ title: 'Aperçu PDF généré', description: 'Imprimez pour tester sur votre papier.' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Génération de l\'aperçu impossible.' });
    } finally {
      setPreviewing(false);
    }
  };

  const preset = (name, vals) => (
    <button
      key={name}
      onClick={() => { setPrimary(vals[0]); setSecondary(vals[1]); setAccent(vals[2]); setHeaderBgColor(vals[0]); setFooterBgColor(vals[0]); }}
      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#EFF0F6] hover:border-[#4F46E5] transition-colors"
      title={name}
    >
      <span className="flex">
        <span className="w-4 h-4 rounded-l" style={{ background: vals[0] }} />
        <span className="w-4 h-4" style={{ background: vals[1] }} />
        <span className="w-4 h-4 rounded-r" style={{ background: vals[2] }} />
      </span>
      <span className="text-[11.5px] font-semibold text-[#4A4B63]">{name}</span>
    </button>
  );

  const palettes = [
    { name: 'IFAAP (défaut)', v: ['#4F46E5', '#14B8A6', '#F59E0B'] },
    { name: 'Océan', v: ['#0369A1', '#0EA5E9', '#14B8A6'] },
    { name: 'Forêt', v: ['#0F766E', '#84CC16', '#F59E0B'] },
    { name: 'Aurore', v: ['#EC4899', '#F59E0B', '#4F46E5'] },
    { name: 'Corporate', v: ['#1E3A8A', '#3B82F6', '#64748B'] },
    { name: 'Terre', v: ['#92400E', '#CA8A04', '#0F766E'] },
    { name: 'Violet doux', v: ['#7C3AED', '#818CF8', '#F472B6'] },
    { name: 'Nuit', v: ['#1A1B2E', '#4F46E5', '#EC4899'] },
  ];

  const HexColorInput = ({ label, value, setValue, testId, compact }) => {
    const stripped = (value || '').replace(/^#/, '').toUpperCase();
    const onTextChange = (e) => {
      const raw = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6).toUpperCase();
      setValue('#' + raw.padEnd(6, '0'));
    };
    return (
      <div>
        {label && <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">{label}</label>}
        <div className="flex items-center gap-2">
          <input type="color" value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} rounded-lg border border-[#EFF0F6] cursor-pointer shrink-0`} />
          <div className={`flex-1 ${compact ? 'h-9' : 'h-10'} rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] flex items-center overflow-hidden focus-within:border-[#4F46E5] focus-within:bg-white`}>
            <span className="px-3 h-full flex items-center text-[#8E8FA5] font-mono text-[13.5px] border-r border-[#EFF0F6] bg-[#EFF0F6]/60 select-none">#</span>
            <input data-testid={testId} value={stripped} onChange={onTextChange} placeholder="7C5CFC" maxLength={6} className="flex-1 h-full px-3 bg-transparent text-[13.5px] font-mono outline-none uppercase" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="soft-card p-6 space-y-6">
      <div>
        <h3 className="text-[17px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Charte graphique</h3>
        <p className="text-[13px] text-[#8E8FA5]">Définissez l'identité visuelle qui s'appliquera sur les documents PDF (devis, factures, convocations, fiches programmes) et l'extranet.</p>
      </div>

      <div>
        <div className="text-[12.5px] font-semibold text-[#4A4B63] mb-2">Palettes prédéfinies</div>
        <div className="flex flex-wrap gap-2">{palettes.map((p) => preset(p.name, p.v))}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <HexColorInput label="Principale" value={primary} setValue={setPrimary} testId="branding-primary" />
        <HexColorInput label="Secondaire" value={secondary} setValue={setSecondary} testId="branding-secondary" />
        <HexColorInput label="Accentuation" value={accent} setValue={setAccent} testId="branding-accent" />
        <HexColorInput label="Texte principal" value={textColor} setValue={setTextColor} testId="branding-text" />
        <HexColorInput label="Fond de page" value={bgColor} setValue={setBgColor} testId="branding-bg" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Toggle testId="branding-show-logo" checked={showLogo} onChange={setShowLogo} label="Afficher le logo en en-tête (à gauche)" description="Si activé, le nom d'organisme n'est pas réaffiché à côté." />
          <label className={`block h-28 rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] flex items-center justify-center cursor-pointer hover:bg-[#EEF2FF] transition-colors ${!showLogo ? 'opacity-50' : ''}`}>
            {logoPrev ? <img src={logoPrev} alt="logo" className="max-h-20 max-w-[200px] object-contain" /> : <span className="text-[13px] text-[#4F46E5] font-medium">Choisir un logo (PNG · 400 Ko max)</span>}
            <input data-testid="branding-logo-input" type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], setLogoB64, setLogoPrev, 400)} className="hidden" />
          </label>
          {logoPrev && <button onClick={() => { setLogoB64(null); setLogoPrev(null); }} className="text-[12px] text-[#B91C1C] font-semibold">Retirer le logo</button>}
        </div>
        <div className="space-y-3">
          <Toggle testId="branding-show-background" checked={showBackground} onChange={setShowBackground} label="Appliquer le papier à en-tête (fond A4)" description="Désactivez temporairement sans supprimer l'image uploadée." />
          <label className={`block h-28 rounded-xl border-2 border-dashed border-[#C7D2FE] bg-[#FAFAFE] flex items-center justify-center cursor-pointer hover:bg-[#EEF2FF] transition-colors relative overflow-hidden ${!showBackground ? 'opacity-50' : ''}`}>
            {backgroundPrev ? <img src={backgroundPrev} alt="bg" className="max-h-20 object-contain" /> : <span className="text-[13px] text-[#4F46E5] font-medium">Choisir un fond A4 (1 Mo max)</span>}
            <input data-testid="branding-bg-input" type="file" accept="image/*" onChange={(e) => readFile(e.target.files?.[0], setBackgroundB64, setBackgroundPrev, 1000)} className="hidden" />
          </label>
          {backgroundPrev && <button onClick={() => { setBackgroundB64(null); setBackgroundPrev(null); }} className="text-[12px] text-[#B91C1C] font-semibold">Retirer le fond</button>}
          <div className={`pt-2 ${!showBackground ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5] mb-1.5">Mode d'affichage du fond</div>
            <select
              value={backgroundMode}
              onChange={(e) => setBackgroundMode(e.target.value)}
              data-testid="branding-bg-mode"
              className="w-full h-10 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[12.5px] focus:border-[#4F46E5] outline-none"
            >
              <option value="full_page">Pleine page (étiré bord à bord)</option>
              <option value="preserve_margins">Respecter les marges (recommandé)</option>
              <option value="logo_top">Logo en haut (50×22 mm)</option>
              <option value="watermark_center">Filigrane centré (12% opacité)</option>
            </select>
            <div className="text-[11px] text-[#6B6C85] mt-1.5 leading-snug">
              {backgroundMode === 'full_page' && '⚠️ Le fond est étiré sur toute la page. Si votre logo est dedans, il peut masquer le contenu.'}
              {backgroundMode === 'preserve_margins' && 'Le fond respecte les marges définies ci-dessous et ne masque jamais le texte.'}
              {backgroundMode === 'logo_top' && 'Idéal si votre image est un logo : affiché en petit au sommet, centré.'}
              {backgroundMode === 'watermark_center' && 'Le fond s\'affiche en filigrane transparent, sous le contenu.'}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[#EFF0F6] p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-3">
            <Toggle testId="branding-show-header" checked={showHeader} onChange={setShowHeader} label="Afficher l'en-tête IFAAP" description="Désactivez pour imprimer sur papier à en-tête pré-existant." />
            <div className={showHeader ? 'space-y-3' : 'space-y-3 opacity-50 pointer-events-none'}>
              <Toggle testId="branding-header-bg-enabled" checked={headerBgEnabled} onChange={setHeaderBgEnabled} label="Bandeau de couleur en fond" description="Fond coloré sous le logo / titre." />
              <div className={headerBgEnabled ? '' : 'opacity-50 pointer-events-none'}>
                <HexColorInput label="Couleur du fond" value={headerBgColor} setValue={setHeaderBgColor} testId="branding-header-bg-color" compact />
              </div>
              <HexColorInput label="Couleur du texte" value={headerTextColor} setValue={setHeaderTextColor} testId="branding-header-text-color" compact />
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Texte sous le bandeau</label>
                <input data-testid="branding-header-text" value={headerText} onChange={(e) => setHeaderText(e.target.value)} placeholder="Ex. Organisme certifié Qualiopi — SIRET 000 000 000 00000" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] outline-none focus:bg-white focus:border-[#4F46E5]" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Hauteur du bandeau : <span className="text-[#4F46E5] font-bold">{headerHeight} mm</span></label>
                <input type="range" min="8" max="40" step="1" value={headerHeight} onChange={(e) => setHeaderHeight(Number(e.target.value))} data-testid="branding-header-height" className="w-full accent-[#4F46E5]" />
                <div className="text-[10.5px] text-[#8E8FA5]">Indépendant du logo — la taille du logo reste fixe.</div>
              </div>
            </div>
            {/* Marge supérieure — TOUJOURS ajustable, même sans en-tête */}
            <div>
              <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Marge supérieure : <span className="text-[#4F46E5] font-bold">{marginTop} mm</span></label>
              <input type="range" min="8" max="60" step="1" value={marginTop} onChange={(e) => setMarginTop(Number(e.target.value))} data-testid="branding-margin-top" className="w-full accent-[#4F46E5]" />
              <div className="text-[10.5px] text-[#8E8FA5]">Toujours réglable — espace vide entre l'en-tête et le contenu (ou depuis le bord si en-tête masqué).</div>
            </div>
          </div>
          <div className="space-y-3">
            <Toggle testId="branding-show-footer" checked={showFooter} onChange={setShowFooter} label="Afficher le pied de page" description="Désactivez si votre papier en comporte déjà un." />
            <div className={showFooter ? 'space-y-3' : 'space-y-3 opacity-50 pointer-events-none'}>
              <Toggle testId="branding-footer-bg-enabled" checked={footerBgEnabled} onChange={setFooterBgEnabled} label="Bandeau de couleur en fond" description="Fond coloré sous le texte." />
              <div className={footerBgEnabled ? '' : 'opacity-50 pointer-events-none'}>
                <HexColorInput label="Couleur du fond" value={footerBgColor} setValue={setFooterBgColor} testId="branding-footer-bg-color" compact />
              </div>
              <HexColorInput label="Couleur du texte" value={footerTextColor} setValue={setFooterTextColor} testId="branding-footer-text-color" compact />
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Contenu (2 lignes max)</label>
                <textarea data-testid="branding-footer-text" value={footerText} onChange={(e) => setFooterText(e.target.value)} rows={2} placeholder={"IFAAP — 12 rue Exemple, 75011 Paris\nSIRET 000 000 000 · DA 00 00 00000 00"} className="w-full px-3.5 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] outline-none focus:bg-white focus:border-[#4F46E5] resize-none" />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Hauteur du bandeau : <span className="text-[#4F46E5] font-bold">{footerHeight} mm</span></label>
                <input type="range" min="8" max="40" step="1" value={footerHeight} onChange={(e) => setFooterHeight(Number(e.target.value))} data-testid="branding-footer-height" className="w-full accent-[#4F46E5]" />
                <div className="text-[10.5px] text-[#8E8FA5]">Utilisé quand le bandeau de couleur est activé.</div>
              </div>
            </div>
            {/* Marge inférieure — TOUJOURS ajustable */}
            <div>
              <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Marge inférieure : <span className="text-[#4F46E5] font-bold">{marginBottom} mm</span></label>
              <input type="range" min="8" max="60" step="1" value={marginBottom} onChange={(e) => setMarginBottom(Number(e.target.value))} data-testid="branding-margin-bottom" className="w-full accent-[#4F46E5]" />
              <div className="text-[10.5px] text-[#8E8FA5]">Toujours réglable — espace vide entre le contenu et le pied de page (ou le bord si pied masqué).</div>
            </div>
            {/* Marges gauche / droite */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Marge gauche : <span className="text-[#4F46E5] font-bold">{marginLeft} mm</span></label>
                <input type="range" min="8" max="40" step="1" value={marginLeft} onChange={(e) => setMarginLeft(Number(e.target.value))} data-testid="branding-margin-left" className="w-full accent-[#4F46E5]" />
                <div className="text-[10.5px] text-[#8E8FA5]">Espace vide sur le bord gauche du papier.</div>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">Marge droite : <span className="text-[#4F46E5] font-bold">{marginRight} mm</span></label>
                <input type="range" min="8" max="40" step="1" value={marginRight} onChange={(e) => setMarginRight(Number(e.target.value))} data-testid="branding-margin-right" className="w-full accent-[#4F46E5]" />
                <div className="text-[10.5px] text-[#8E8FA5]">Espace vide sur le bord droit du papier.</div>
              </div>
            </div>
            {/* Presets marges (raccourcis) */}
            <div className="rounded-xl border border-[#EDE8FF] bg-[#FAFAFE] p-3">
              <div className="text-[11.5px] font-bold text-[#4A4B63] uppercase tracking-wide mb-2">Presets marges</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="preset-standard" onClick={() => { setMarginTop(14); setMarginBottom(18); setMarginLeft(18); setMarginRight(18); }} className="h-8 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[11.5px] font-semibold hover:bg-[#EEF2FF]">
                  Standard A4 (14 / 18 / 18 / 18)
                </button>
                <button type="button" data-testid="preset-letterhead" onClick={() => { setMarginTop(40); setMarginBottom(18); setMarginLeft(18); setMarginRight(18); }} className="h-8 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[11.5px] font-semibold hover:bg-[#EEF2FF]">
                  Papier à en-tête (40 / 18 / 18 / 18)
                </button>
                <button type="button" data-testid="preset-eco" onClick={() => { setMarginTop(8); setMarginBottom(8); setMarginLeft(12); setMarginRight(12); }} className="h-8 px-3 rounded-lg bg-white border border-[#C7D2FE] text-[#4F46E5] text-[11.5px] font-semibold hover:bg-[#EEF2FF]">
                  Économie d'encre (8 / 8 / 12 / 12)
                </button>
              </div>
              <p className="text-[10.5px] text-[#8E8FA5] mt-2">Les valeurs sont appliquées instantanément. N'oublie pas d'Enregistrer en bas de page.</p>
            </div>
          </div>
        </div>
        <div className="pt-4 border-t border-[#F3F4F8] space-y-2">
          <label className="text-[12.5px] font-semibold text-[#4A4B63] block">Filigrane (optionnel)</label>
          <input data-testid="branding-watermark" value={watermark} onChange={(e) => setWatermark(e.target.value)} placeholder="Ex. CONFIDENTIEL ou BROUILLON" className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] outline-none focus:bg-white focus:border-[#4F46E5]" />
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 pt-1 ${watermark.trim() ? '' : 'opacity-50 pointer-events-none'}`}>
            <div>
              <label className="text-[11.5px] text-[#4A4B63] font-semibold block mb-1">
                Opacité : <span className="text-[#4F46E5] font-bold">{Math.round(watermarkOpacity * 100)}%</span>
              </label>
              <input
                type="range" min="2" max="50" step="1"
                value={Math.round(watermarkOpacity * 100)}
                onChange={(e) => setWatermarkOpacity(Number(e.target.value) / 100)}
                data-testid="branding-watermark-opacity"
                className="w-full accent-[#4F46E5]"
              />
            </div>
            <div>
              <label className="text-[11.5px] text-[#4A4B63] font-semibold block mb-1">
                Taille : <span className="text-[#4F46E5] font-bold">{watermarkSize} pt</span>
              </label>
              <input
                type="range" min="24" max="200" step="2"
                value={watermarkSize}
                onChange={(e) => setWatermarkSize(Number(e.target.value))}
                data-testid="branding-watermark-size"
                className="w-full accent-[#4F46E5]"
              />
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[12.5px] font-semibold text-[#4A4B63]">Aperçu document</div>
          <div className="flex items-center bg-[#F6F7FB] rounded-lg p-1">
            <button data-testid="preview-kind-invoice" onClick={() => setPreviewKind('invoice')} className={`px-3 h-8 rounded-md text-[12px] font-semibold transition-colors ${previewKind === 'invoice' ? 'bg-white shadow-sm text-[#4F46E5]' : 'text-[#6B6C85]'}`}>Facture</button>
            <button data-testid="preview-kind-quote" onClick={() => setPreviewKind('quote')} className={`px-3 h-8 rounded-md text-[12px] font-semibold transition-colors ${previewKind === 'quote' ? 'bg-white shadow-sm text-[#4F46E5]' : 'text-[#6B6C85]'}`}>Devis</button>
          </div>
        </div>
        <DocumentPreview
          kind={previewKind}
          bgColor={bgColor} textColor={textColor} primary={primary} secondary={secondary} accent={accent}
          showHeader={showHeader} headerBgEnabled={headerBgEnabled} headerBgColor={headerBgColor} headerTextColor={headerTextColor} headerText={headerText}
          showFooter={showFooter} footerBgEnabled={footerBgEnabled} footerBgColor={footerBgColor} footerTextColor={footerTextColor} footerText={footerText}
          showLogo={showLogo} logoPrev={logoPrev}
          showBackground={showBackground} backgroundPrev={backgroundPrev}
          orgName="IFAAP" marginTop={marginTop} marginBottom={marginBottom} marginLeft={marginLeft} marginRight={marginRight}
          headerHeight={headerHeight} footerHeight={footerHeight}
          watermark={watermark} watermarkOpacity={watermarkOpacity} watermarkSize={watermarkSize}
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          data-testid="branding-preview-btn"
          onClick={visualize}
          disabled={previewing || saving}
          className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#EEF2FF] disabled:opacity-60"
          title="Enregistre puis génère un PDF d'aperçu pour test d'impression"
        >
          <Eye className="w-4 h-4" />
          {previewing ? 'Génération…' : 'Visualiser'}
        </button>
        <button data-testid="branding-save-btn" onClick={save} disabled={saving} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

function DocumentPreview(p) {
  const isInvoice = p.kind === 'invoice';
  const W = 620; const H = Math.round(W * 1.414);
  const mm = W / 210;
  const headerH = p.headerHeight ?? 14;
  const footerH = p.footerHeight ?? 12;
  const items = isInvoice ? [
    { label: 'Développement Web Moderne — 3 apprenants', qty: 3, unit: 1200, total: 3600 },
    { label: 'Frais pédagogiques', qty: 1, unit: 250, total: 250 },
  ] : [
    { label: 'Formation React Avancé (35h) — 5 apprenants', qty: 5, unit: 980, total: 4900 },
    { label: 'Support pédagogique personnalisé', qty: 1, unit: 300, total: 300 },
  ];
  const ht = items.reduce((s, i) => s + i.total, 0);
  const tva = Math.round(ht * 0.2);
  const ttc = ht + tva;

  return (
    <div className="rounded-xl shadow-sm border border-[#EFF0F6] overflow-hidden mx-auto relative" style={{ width: W, height: H, background: p.bgColor, color: p.textColor, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {p.showBackground && p.backgroundPrev && (
        <img src={p.backgroundPrev} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      )}
      {/* Dashed content-zone guide (purely informative — visible only in preview) */}
      <div
        className="absolute pointer-events-none rounded-[2px]"
        style={{
          top: (p.marginTop + (p.showHeader ? headerH : 0)) * mm,
          left: (p.marginLeft ?? 18) * mm,
          right: (p.marginRight ?? 18) * mm,
          bottom: (p.showFooter ? (p.footerBgEnabled ? Math.max(footerH, p.marginBottom) : p.marginBottom) : p.marginBottom) * mm,
          border: '1px dashed rgba(124, 92, 252, 0.45)',
          background: 'transparent',
          zIndex: 1,
        }}
        aria-hidden="true"
      />
      {p.watermark && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none font-black select-none -rotate-[30deg]" style={{ color: p.primary, opacity: p.watermarkOpacity, fontSize: (p.watermarkSize ?? 72) }}>
          {p.watermark}
        </div>
      )}
      {p.showHeader && (
        <>
          <div className="absolute left-0 right-0 flex items-center px-5" style={{ top: 0, height: headerH * mm, background: p.headerBgEnabled ? p.headerBgColor : 'transparent', color: p.headerTextColor || (p.headerBgEnabled ? '#fff' : p.textColor) }}>
            {p.showLogo && p.logoPrev ? (
              <img src={p.logoPrev} alt="logo" style={{ height: 10 * mm, width: 'auto', maxWidth: 22 * mm }} className="object-contain" />
            ) : (
              <div className="text-[15px] font-bold">{p.orgName}</div>
            )}
          </div>
          {p.headerText && (
            <div className="absolute left-0 right-0 px-5 italic text-[10px]" style={{ top: headerH * mm + 2, color: '#8E8FA5' }}>{p.headerText}</div>
          )}
        </>
      )}
      <div
        className="absolute"
        style={{
          top: (p.marginTop + (p.showHeader ? headerH : 0)) * mm,
          left: (p.marginLeft ?? 18) * mm,
          right: (p.marginRight ?? 18) * mm,
          bottom: (p.showFooter ? (p.footerBgEnabled ? Math.max(footerH, p.marginBottom) : p.marginBottom) : p.marginBottom) * mm,
        }}
      >
        <div className="text-[22px] font-extrabold" style={{ color: p.textColor }}>
          {isInvoice ? 'Facture' : 'Devis'} <span className="text-[#8E8FA5] text-[14px] font-semibold">{isInvoice ? 'F-2026-0042' : 'D-2026-0115'}</span>
        </div>
        <div className="h-px w-full mt-2" style={{ background: '#EFF0F6' }} />
        <div className="mt-4 grid grid-cols-2 gap-6 text-[10.5px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#8E8FA5' }}>Émis pour</div>
            <div className="font-bold" style={{ color: p.textColor }}>TechCorp France</div>
            <div style={{ color: '#6B6C85' }}>142 avenue des Champs-Élysées<br/>75008 Paris</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: '#8E8FA5' }}>Informations</div>
            <div style={{ color: p.textColor }}>Date : {isInvoice ? '22/04/2026' : '20/04/2026'}</div>
            <div style={{ color: p.textColor }}>{isInvoice ? 'Échéance : 22/05/2026' : "Valide jusqu'au : 20/05/2026"}</div>
          </div>
        </div>
        <table className="w-full mt-5 text-[10.5px]">
          <thead>
            <tr style={{ background: '#FAFAFE', color: '#8E8FA5' }} className="text-[9.5px] uppercase tracking-wider">
              <th className="py-2 px-3 text-left font-semibold">Désignation</th>
              <th className="py-2 px-3 text-right font-semibold w-14">Qté</th>
              <th className="py-2 px-3 text-right font-semibold w-24">PU HT</th>
              <th className="py-2 px-3 text-right font-semibold w-24">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={`${it.label}-${i}`} className="border-b" style={{ borderColor: '#F3F4F8' }}>
                <td className="py-2.5 px-3" style={{ color: p.textColor }}>{it.label}</td>
                <td className="py-2.5 px-3 text-right" style={{ color: p.textColor }}>{it.qty}</td>
                <td className="py-2.5 px-3 text-right" style={{ color: p.textColor }}>{it.unit.toLocaleString('fr-FR')} €</td>
                <td className="py-2.5 px-3 text-right font-semibold" style={{ color: p.textColor }}>{it.total.toLocaleString('fr-FR')} €</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <div className="w-[240px] text-[10.5px] space-y-1">
            <div className="flex justify-between py-1"><span style={{ color: '#8E8FA5' }}>Total HT</span><span style={{ color: p.textColor }}>{ht.toLocaleString('fr-FR')} €</span></div>
            <div className="flex justify-between py-1"><span style={{ color: '#8E8FA5' }}>TVA 20%</span><span style={{ color: p.textColor }}>{tva.toLocaleString('fr-FR')} €</span></div>
            <div className="flex justify-between py-2 px-3 rounded-lg mt-1" style={{ background: p.primary, color: '#fff' }}>
              <span className="font-bold">Total TTC</span><span className="font-bold">{ttc.toLocaleString('fr-FR')} €</span>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <span className="inline-block px-3 py-1 rounded text-[10.5px] font-semibold text-white" style={{ background: isInvoice ? p.secondary : p.accent }}>
            {isInvoice ? 'Payable sous 30 jours' : 'Devis acceptable sous 30 jours'}
          </span>
        </div>
      </div>
      {p.showFooter && p.footerText && (
        p.footerBgEnabled ? (
          <div className="absolute left-0 right-0 flex items-center justify-center text-center px-6 whitespace-pre-line" style={{ bottom: 0, height: footerH * mm, background: p.footerBgColor, color: p.footerTextColor || '#fff', fontSize: 9, lineHeight: '12px' }}>
            {p.footerText}
          </div>
        ) : (
          // Plain footer: INSIDE the dashed content frame, anchored just above margin_bottom baseline
          <div
            className="absolute flex flex-col items-center justify-end text-center whitespace-pre-line"
            style={{
              left: (p.marginLeft ?? 18) * mm,
              right: (p.marginRight ?? 18) * mm,
              bottom: (p.marginBottom + 1) * mm,
              color: p.footerTextColor || '#8E8FA5',
              fontSize: 9,
              lineHeight: '11px',
            }}
          >
            {p.footerText}
          </div>
        )
      )}
    </div>
  );
}

// =============================================================================
// Qualiopi Templates: 4 editable blocks used in programme datasheet PDFs
// =============================================================================
const QUALIOPI_DEFAULTS = {
  methods: "• Alternance d'apports théoriques structurés et d'exercices pratiques appliqués.\n• Études de cas réels et mises en situation professionnelles.\n• Supports pédagogiques numériques remis à chaque apprenant.\n• Travaux individuels et collaboratifs, corrigés en direct par le formateur.\n• Questions-réponses et temps d'échange réservés à chaque demi-journée.",
  evaluation: "• <b>Évaluation diagnostique</b> en début de formation (quiz de positionnement).\n• <b>Évaluation formative continue</b> via exercices pratiques et études de cas.\n• <b>Évaluation sommative</b> en fin de parcours : QCM final et/ou mise en situation.\n• Remise d'une <b>attestation de fin de formation</b> précisant les compétences acquises.\n• Questionnaire de satisfaction à chaud et à froid (J+60).",
  accessibility: "La formation est accessible aux personnes en situation de handicap. Nous étudions avec vous les adaptations nécessaires (supports, rythme, accessibilité des locaux, interprétariat LSF, etc.) afin de garantir l'égalité d'accès. Notre référent handicap vous accompagne tout au long de votre parcours.",
  registration: "• <b>Inscription</b> par email ou via notre extranet, au plus tard 14 jours avant le début de la session.\n• <b>Convention de formation</b> signée électroniquement.\n• <b>Convocation</b> transmise 7 jours avant, incluant programme détaillé, adresse et horaires.\n• <b>Financement</b> : OPCO, CPF, plan de développement des compétences, auto-financement.",
};

const QUALIOPI_FIELDS = [
  { key: 'methods', label: 'Méthodes pédagogiques', accent: '#4F46E5', help: "Décrivez les méthodes pédagogiques employées (ateliers, études de cas, alternance…). Reproduit tel quel en section 1 de la page 2 des fiches PDF." },
  { key: 'evaluation', label: "Modalités d'évaluation", accent: '#14B8A6', help: "Détaillez les modalités d'évaluation Qualiopi-conformes (diagnostique, formative, sommative)." },
  { key: 'accessibility', label: 'Accessibilité handicap', accent: '#F59E0B', help: "Texte affiché en section Accessibilité. Mentionnez votre référent handicap et les adaptations possibles." },
  { key: 'registration', label: "Formalités d'inscription et financement", accent: '#4F46E5', help: "Procédure d'inscription, convention, convocation, moyens de financement." },
];

// Les valeurs courtes que vos fiches portaient **à l'identique** : sur les
// treize fiches remplies, le délai d'accès, ce qui est délivré et l'unité de
// prix étaient la même phrase tapée douze fois ; la qualification et les
// moyens, dix fois sur douze. Dites-les une fois, les fiches muettes les
// reprennent — et taper dans le champ d'une fiche la décroche pour toujours.
const QUALIOPI_COURTS = [
  { key: 'delai_acces', label: "Délai d'accès", accent: '#14B8A6', help: "Le délai entre la demande et l'entrée en formation (indicateur 4).", exemple: 'Délai habituel de 15 jours à un mois entre la demande et l’entrée en formation.' },
  { key: 'sanction', label: 'Ce qui est délivré', accent: '#F59E0B', help: "Ce que l'apprenant obtient à l'issue : attestation, certificat, titre.", exemple: 'Certificat IFAAP après participation à l’ensemble de la formation.' },
  { key: 'prix_unite', label: "Le prix s'entend", accent: '#4F46E5', help: "Un tarif sans son unité se lit comme un tarif de groupe une fois sur deux.", exemple: 'par personne' },
  { key: 'qualification_formateur', label: 'Qualification du formateur', accent: '#14B8A6', help: "Ce qui rend le formateur compétent sur ce sujet (indicateur 21).", exemple: 'Formateur certifié' },
  { key: 'moyens_pedagogiques', label: 'Moyens et matériel', accent: '#F59E0B', help: "Les moyens techniques mis à disposition (indicateur 17).", exemple: 'Salle équipée, accès Internet, support remis à chaque participant.' },
  { key: 'tarif_mention', label: 'Ce que le tarif veut dire', accent: '#4F46E5', help: "Sous le tarif de référence de chaque fiche : pourquoi le devis peut en différer. Le tarif reste une base de calcul.", exemple: "dégressif selon l'effectif et les modalités" },
];

function QualiopiTemplatesSection() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/orgs/qualiopi-templates')
      .then(({ data }) => setValues(data))
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/orgs/qualiopi-templates', values);
      toast({ title: 'Templates enregistrés', description: 'Les fiches programmes PDF utiliseront ces textes par défaut.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Enregistrement impossible' });
    }
    setSaving(false);
  };

  const handlePreview = async () => {
    try {
      const { data } = await api.post('/orgs/qualiopi-templates/preview-pdf', values, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast({ title: 'Aperçu', description: e?.response?.data?.detail || 'Impossible de générer l\'aperçu' });
    }
  };

  if (loading) return <div className="soft-card p-6 text-[13px] text-[#6B6C85]">Chargement…</div>;

  return (
    <div className="soft-card p-6" data-testid="qualiopi-templates-section">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Templates Qualiopi</h2>
          <p className="text-[12.5px] text-[#6B6C85] mt-1 max-w-xl">
            Ce que votre organisme dit une fois pour toutes ses fiches programme. Une fiche muette le reprend ;
            une fiche qui écrit le sien la décroche, et rien n&rsquo;est recopié — corriger ici corrige tous les
            documents, y compris ceux déjà édités.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="qualiopi-save" className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <button onClick={handlePreview} data-testid="qualiopi-preview" className="h-9 px-3 rounded-xl bg-white border border-[#EFF0F6] text-[12.5px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB] inline-flex items-center gap-1.5">
          <Eye className="w-3.5 h-3.5" /> Prévisualiser le PDF programme
        </button>
        <span className="text-[11.5px] text-[#8E8FA5]">Aperçu en temps réel sans sauvegarde</span>
      </div>

      <div className="space-y-6">
        {QUALIOPI_FIELDS.map((f) => (
          <div key={f.key} data-testid={`qualiopi-block-${f.key}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-1 h-4 rounded-sm" style={{ background: f.accent }} />
              <h3 className="text-[14px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{f.label}</h3>
            </div>
            <p className="text-[11.5px] text-[#8E8FA5] mb-2">{f.help}</p>
            <RichEditor
              value={values[f.key] || ''}
              onChange={(v) => setValues((vv) => ({ ...vv, [f.key]: v }))}
              defaultValue={QUALIOPI_DEFAULTS[f.key]}
              placeholder={QUALIOPI_DEFAULTS[f.key]}
              testId={`qualiopi-editor-${f.key}`}
              rows={6}
            />
          </div>
        ))}
      </div>

      <div className="mt-7 pt-6 border-t border-[#EFF0F6]">
        <h3 className="text-[14px] font-semibold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Les valeurs que toutes vos fiches partagent
        </h3>
        <p className="text-[11.5px] text-[#8E8FA5] mb-4 max-w-2xl">
          Sur vos treize fiches remplies, ces valeurs étaient rigoureusement identiques — la même phrase tapée
          douze fois. Dites-les ici, et les fiches qui ne disent rien les reprennent.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUALIOPI_COURTS.map((f) => (
            <div key={f.key} data-testid={`qualiopi-court-${f.key}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1 h-3.5 rounded-sm" style={{ background: f.accent }} />
                <span className="text-[13px] font-semibold text-[#1A1B2E]">{f.label}</span>
              </div>
              <input
                value={values[f.key] || ''}
                onChange={(e) => setValues((vv) => ({ ...vv, [f.key]: e.target.value }))}
                placeholder={f.exemple}
                data-testid={`qualiopi-court-input-${f.key}`}
                className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none transition-colors"
              />
              <p className="text-[11px] text-[#8E8FA5] mt-1">{f.help}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 p-3 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] text-[11.5px] text-[#4A4B63] flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-[#4F46E5] shrink-0 mt-0.5" />
        <div>
          <strong>Ordre de priorité</strong> pour chaque bloc dans le PDF programme :
          <ol className="ml-5 mt-1 list-decimal space-y-0.5">
            <li>Champ spécifique du programme (si rempli)</li>
            <li>Template de l'organisme ci-dessus</li>
            <li>Texte par défaut Qualiopi intégré</li>
          </ol>
        </div>
      </div>
    </div>
  );
}


function EmailTemplatesSection({ userEmail }) {
  const [list, setList] = useState([]);
  const [userTpls, setUserTpls] = useState([]); // Vague 97.5 — user-created custom templates
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null); // Vague 97.5
  const [current, setCurrent] = useState(null); // active template (system OR user)
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/email-templates').then(({ data }) => data).catch(() => []),
      api.get('/user/email-templates').then(({ data }) => data).catch(() => []),
    ]).then(([sys, usr]) => {
      setList(sys);
      setUserTpls(usr);
      if (sys.length) setSelectedType(sys[0].type);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      const found = userTpls.find((t) => t.id === selectedUserId);
      if (found) {
        setCurrent({ ...found, kind: 'user', label: found.name, body_html: found.body_html || found.body || '' });
      }
      return;
    }
    if (!selectedType) return;
    const found = list.find((t) => t.type === selectedType);
    setCurrent(found ? { ...found, kind: 'system' } : null);
  }, [selectedType, selectedUserId, list, userTpls]);

  useEffect(() => { setTestTo(userEmail || ''); }, [userEmail]);

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      if (current.kind === 'user') {
        const payload = {
          name: current.label || current.name || 'Template sans nom',
          subject: current.subject || '',
          body: current.body_html || '',
        };
        const { data } = await api.put(`/user/email-templates/${current.id}`, payload);
        setUserTpls((prev) => prev.map((t) => t.id === data.id ? data : t));
        setCurrent({ ...data, kind: 'user', label: data.name, body_html: data.body });
        toast({ title: 'Modèle enregistré', description: data.name });
      } else {
        const { data } = await api.put(`/email-templates/${current.type}`, {
          subject: current.subject, title: current.title, body_html: current.body_html,
        });
        setList((prev) => prev.map((t) => t.type === data.type ? data : t));
        setCurrent({ ...data, kind: 'system' });
        toast({ title: 'Template enregistré', description: data.label || data.name });
      }
    } catch {
      toast({ title: 'Erreur', description: "Impossible d'enregistrer." });
    } finally {
      setSaving(false);
    }
  };

  const createUserTpl = async () => {
    try {
      const { data } = await api.post('/user/email-templates', {
        name: 'Nouveau modèle',
        subject: 'Objet',
        body_html: '<p>Bonjour,</p><p>Votre message ici…</p>',
        description: '',
      });
      setUserTpls((prev) => [data, ...prev]);
      setSelectedType(null);
      setSelectedUserId(data.id);
      toast({ title: 'Modèle créé', description: 'Personnalisez-le puis cliquez sur Enregistrer.' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Création impossible';
      toast({ title: 'Erreur', description: msg });
    }
  };

  const deleteUserTpl = async () => {
    if (!current || current.kind !== 'user') return;
    if (!window.confirm(`Supprimer le modèle « ${current.label || current.name} » ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/user/email-templates/${current.id}`);
      setUserTpls((prev) => prev.filter((t) => t.id !== current.id));
      setSelectedUserId(null);
      if (list.length) setSelectedType(list[0].type);
      toast({ title: 'Modèle supprimé' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Suppression impossible';
      toast({ title: 'Erreur', description: msg });
    }
  };

  const reset = async () => {
    if (!current || current.kind === 'user') return;
    if (!window.confirm('Restaurer le template par défaut ? Vos modifications seront perdues.')) return;
    try {
      const { data } = await api.post(`/email-templates/${current.type}/reset`);
      setList((prev) => prev.map((t) => t.type === data.type ? data : t));
      setCurrent({ ...data, kind: 'system' });
      toast({ title: 'Template restauré' });
    } catch {
      toast({ title: 'Erreur' });
    }
  };

  const sendTest = async () => {
    if (!current || !testTo) return;
    if (current.kind === 'user') {
      toast({ title: 'Indisponible', description: "L'envoi de test est réservé aux templates système. Utilisez l'EmailComposer depuis une fiche entreprise pour tester votre modèle." });
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/email-templates/${current.type}/send-test`, { to: testTo });
      // Un échec ne se dit pas « simulé » : c'est ce mot qui a laissé croire
      // pendant six semaines que l'envoi était désactivé exprès.
      toast({
        title: data.sent ? 'Email de test envoyé' : "L'email de test n'est pas parti",
        description: data.sent
          ? `À : ${data.sent_to}`
          : data.error || `À : ${data.sent_to} — raison inconnue, voir le journal.`,
      });
    } catch {
      toast({ title: 'Erreur envoi' });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="soft-card p-6 text-[13px] text-[#8E8FA5]">Chargement…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
      <aside className="soft-card p-3 h-fit">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] px-2 py-1.5">Templates système</div>
        {list.map((t) => (
          <button
            key={t.type}
            data-testid={`tpl-tab-${t.type}`}
            onClick={() => { setSelectedUserId(null); setSelectedType(t.type); }}
            className={`w-full text-left flex items-start gap-2 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
              !selectedUserId && selectedType === t.type ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#4A4B63] hover:bg-[#F6F7FB]'
            }`}
          >
            <Mail className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block truncate">{t.label || t.name}</span>
              <span className="block text-[11px] font-normal text-[#8E8FA5] truncate">{t.description}</span>
            </span>
          </button>
        ))}

        {/* Vague 97.5 — User-created custom templates */}
        <div className="mt-4 pt-3 border-t border-[#EFF0F6]">
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5]">Mes modèles</div>
            <button
              type="button"
              data-testid="user-tpl-create-btn"
              onClick={createUserTpl}
              title="Créer un nouveau modèle personnalisé"
              className="w-6 h-6 rounded-md bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#E8E2FD] flex items-center justify-center"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {userTpls.length === 0 ? (
            <div className="text-[11.5px] text-[#8E8FA5] italic px-2 py-2 leading-relaxed">
              Aucun modèle personnalisé. Cliquez sur <span className="font-semibold text-[#4F46E5]">+</span> pour en créer un.
            </div>
          ) : (
            userTpls.map((t) => (
              <button
                key={t.id}
                data-testid={`user-tpl-${t.id}`}
                onClick={() => { setSelectedType(null); setSelectedUserId(t.id); }}
                className={`w-full text-left flex items-start gap-2 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${
                  selectedUserId === t.id ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#4A4B63] hover:bg-[#F6F7FB]'
                }`}
              >
                <FileSignature className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{t.name || 'Sans nom'}</span>
                  <span className="block text-[11px] font-normal text-[#8E8FA5] truncate">{t.subject || '—'}</span>
                </span>
              </button>
            ))
          )}
        </div>
      </aside>

      {current && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 min-h-[640px]">
          <div className="soft-card p-5 flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex-1 min-w-0">
                {current.kind === 'user' ? (
                  <input
                    data-testid="user-tpl-name"
                    value={current.label || ''}
                    onChange={(e) => setCurrent({ ...current, label: e.target.value, name: e.target.value })}
                    placeholder="Nom du modèle"
                    className="w-full text-[16px] font-semibold text-[#1A1B2E] bg-transparent outline-none border-b-2 border-transparent focus:border-[#4F46E5]"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  />
                ) : (
                  <h3 className="text-[16px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{current.label || current.name}</h3>
                )}
                <p className="text-[12px] text-[#8E8FA5] mt-0.5">{current.kind === 'user' ? "Modèle personnalisé — utilisable depuis l'EmailComposer" : current.description}</p>
              </div>
              {current.kind === 'user' ? (
                <button
                  data-testid="user-tpl-delete-btn"
                  onClick={deleteUserTpl}
                  className="h-9 px-3 rounded-lg border border-[#FECACA] text-[12px] text-[#B91C1C] font-semibold flex items-center gap-1.5 hover:bg-[#FEF2F2] shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
              ) : (
                <button
                  data-testid="tpl-reset-btn"
                  onClick={reset}
                  className="h-9 px-3 rounded-lg border border-[#EFF0F6] text-[12px] text-[#4A4B63] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE] shrink-0"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Restaurer
                </button>
              )}
            </div>

            <div className="space-y-3 flex-1 flex flex-col">
              <div>
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Objet de l'email</label>
                <input
                  data-testid="tpl-subject"
                  value={current.subject || ''}
                  onChange={(e) => setCurrent({ ...current, subject: e.target.value })}
                  placeholder="Bienvenue chez {{org_name}} !"
                  className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none"
                />
              </div>

              {current.kind !== 'user' && (
                <div>
                  <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Titre du bandeau coloré (en haut de l'email)</label>
                  <input
                    data-testid="tpl-title"
                    value={current.title || ''}
                    onChange={(e) => setCurrent({ ...current, title: e.target.value })}
                    className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none"
                  />
                </div>
              )}

              <div className="flex-1 flex flex-col">
                <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Contenu</label>
                <RichEmailEditor
                  value={current.body_html || ''}
                  onChange={(html) => setCurrent({ ...current, body_html: html })}
                  variables={current.variables || []}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#EFF0F6]">
                <button
                  data-testid="tpl-save-btn"
                  onClick={save}
                  disabled={saving}
                  className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <div className="flex-1" />
                {current.kind !== 'user' && (
                  <>
                    <input
                      data-testid="tpl-test-to"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="test@exemple.fr"
                      className="h-10 w-52 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] outline-none"
                    />
                    <button
                      data-testid="tpl-send-test-btn"
                      onClick={sendTest}
                      disabled={!testTo || sending}
                      className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#14B8A6] flex items-center gap-2 hover:bg-[#E6FBF7] disabled:opacity-60"
                    >
                      <Send className="w-4 h-4" /> {sending ? 'Envoi…' : 'Test'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Live preview — debounced 350ms, no manual click required */}
          <EmailLivePreview
            template={current}
            subject={current.subject}
            bodyHtml={current.body_html}
            title={current.title}
          />
        </div>
      )}
    </div>
  );
}
// =============================================================================
// Role permissions matrix — Vague 99.8
// =============================================================================
function RolePermissionsSection() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleCopyFrom, setNewRoleCopyFrom] = useState('Formateur');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/role-permissions');
      setData(data);
      setDirty({});
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec de chargement' });
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (role, key) => {
    setData((d) => ({
      ...d,
      permissions: {
        ...d.permissions,
        [role]: { ...d.permissions[role], [key]: !d.permissions[role][key] },
      },
    }));
    setDirty((x) => ({ ...x, [role]: true }));
  };

  const save = async (role) => {
    setSaving(role);
    try {
      await api.put(`/role-permissions/${encodeURIComponent(role)}`, { modules: data.permissions[role] });
      toast({ title: 'Enregistré', description: `Permissions du rôle ${role}` });
      setDirty((x) => ({ ...x, [role]: false }));
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setSaving(null); }
  };

  const createRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post('/roles', { role: name, copy_from: newRoleCopyFrom || null });
      toast({ title: 'Rôle créé', description: `"${name}" est disponible.` });
      setNewRoleName('');
      await load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible' });
    } finally { setCreating(false); }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Supprimer le rôle "${role}" ? Les utilisateurs qui l'ont seront rebasculés sur "Formateur".`)) return;
    try {
      await api.delete(`/roles/${encodeURIComponent(role)}`);
      toast({ title: 'Rôle supprimé', description: role });
      await load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Suppression impossible' });
    }
  };

  if (!data) {
    return (
      <div className="soft-card p-6 flex items-center justify-center" data-testid="perms-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  // Group modules by their `group` field
  const groups = {};
  data.modules.forEach((m) => {
    groups[m.group] = groups[m.group] || [];
    groups[m.group].push(m);
  });

  const systemRoles = data.system_roles || ['Administrateur', 'Formateur', 'Commercial', 'Lecture seule'];
  const customRoles = data.custom_roles || [];

  return (
    <div className="space-y-4" data-testid="role-permissions-section">
      <div className="soft-card p-5">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-[#4F46E5] shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-[#4A4B63] leading-relaxed">
            Configurez précisément les modules accessibles à chaque rôle de votre équipe. Les modifications s'appliquent immédiatement : la barre latérale masque les modules non autorisés et les routes sont bloquées côté backend. Les administrateurs gardent toujours accès aux <strong>Paramètres</strong> (garde-fou).
          </div>
        </div>
      </div>

      {/* Custom role creation bar */}
      <div className="soft-card p-5" data-testid="custom-role-create-card">
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-4 h-4 text-[#4F46E5]" />
          <h3 className="text-[14px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>Créer un rôle personnalisé</h3>
        </div>
        <p className="text-[12px] text-[#6B6C85] mb-3">Exemples : « Assistant », « Comptable », « Responsable pédagogique »… Les permissions seront entièrement paramétrables ci-dessous.</p>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            data-testid="new-role-name"
            type="text"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            placeholder="Nom du rôle (40 caractères max)"
            maxLength={40}
            className="flex-1 h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] outline-none focus:bg-white focus:border-[#4F46E5]"
          />
          <select
            data-testid="new-role-copy-from"
            value={newRoleCopyFrom}
            onChange={(e) => setNewRoleCopyFrom(e.target.value)}
            className="h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] outline-none focus:bg-white focus:border-[#4F46E5]"
            title="Point de départ des permissions"
          >
            {data.roles.map((r) => <option key={r} value={r}>Copier de : {r}</option>)}
          </select>
          <button
            onClick={createRole}
            disabled={creating || !newRoleName.trim()}
            data-testid="create-role-btn"
            className="h-10 px-4 rounded-xl bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50 hover:bg-[#4338CA] transition-colors"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Créer le rôle
          </button>
        </div>
      </div>

      {data.roles.map((role) => {
        const isCustom = customRoles.includes(role);
        const isSystem = systemRoles.includes(role);
        return (
        <div key={role} className="soft-card p-5" data-testid={`perms-role-${role}`}>
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>{role}</h3>
              {isCustom && (
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-[#4F46E5] bg-[#EEF0FF] px-2 py-0.5 rounded-full">Personnalisé</span>
              )}
              {isSystem && (
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-[#6B6C85] bg-[#F3F4F8] px-2 py-0.5 rounded-full">Système</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isCustom && (
                <button
                  onClick={() => deleteRole(role)}
                  data-testid={`perms-delete-${role}`}
                  className="h-9 px-3 rounded-lg border border-[#FCA5A5] text-[#B91C1C] bg-white text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FEF2F2]"
                  title="Supprimer ce rôle personnalisé"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Supprimer
                </button>
              )}
              <button
                onClick={() => save(role)} disabled={!dirty[role] || saving === role}
                data-testid={`perms-save-${role}`}
                className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
              >
                {saving === role ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Enregistrer
              </button>
            </div>
          </div>
          {/* Wave 1u7c — Switched from md:grid-cols-2 (which leaves big gaps when
              one column has more items than the other — e.g. FORMATION with 5 toggles
              vs PILOTAGE with 2) to a responsive 3-column grid with `auto-rows-fr` +
              `h-full` so cards in the same row stretch to identical heights. The
              toggles inside each card use `justify-between` so they all anchor to
              the card's right edge — visually perfectly aligned. */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 auto-rows-fr">
            {Object.entries(groups).map(([grp, mods]) => (
              <div key={grp} className="rounded-xl border border-[#EFF0F6] p-3 bg-[#FAFAFE] h-full flex flex-col">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#4F46E5] mb-2">{grp}</div>
                <div className="space-y-1 flex-1">
                  {mods.map((m) => {
                    const checked = !!data.permissions[role]?.[m.key];
                    const locked = (role === 'Administrateur' && m.key === 'parametres');
                    return (
                      <label key={m.key} className={`flex items-center justify-between gap-3 py-1.5 px-2 rounded-lg text-[12.5px] ${locked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-white'}`}>
                        <span className="flex-1 min-w-0 truncate text-[#1A1B2E]">{m.label}</span>
                        <button type="button" disabled={locked}
                                onClick={() => !locked && toggle(role, m.key)}
                                data-testid={`perm-${role}-${m.key}`}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${checked ? 'bg-[#4F46E5]' : 'bg-[#D1D5E0]'} ${locked ? '' : 'hover:opacity-90'}`}>
                          {/* Wave 1u7d — Sized so the knob has 2 px breathing room on
                              every side of the 24 px track. Previous spec (w-9 h-5 with
                              w-4 knob) was too tight: the box-shadow visually overflowed
                              the right edge when the toggle was on. */}
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                        </button>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      );})}
    </div>
  );
}


// =============================================================================
// Mon Abonnement — OFManager subscription self-management (Vague 99.4)
// =============================================================================
function MonAbonnementSection() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/billing/my-tenant');
      setData(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Impossible de charger les informations d\'abonnement';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post('/billing/customer-portal', {
        return_url: window.location.href,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast({ title: 'Erreur', description: 'URL du portail Stripe indisponible', variant: 'destructive' });
        setOpening(false);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Impossible d\'ouvrir le portail Stripe';
      const message = status === 503
        ? 'Le portail self-service Stripe est désactivé en mode bac à sable. Contactez votre administrateur OFManager.'
        : status === 404
        ? 'Aucun paiement Stripe trouvé pour votre compte. Souscrivez d\'abord à un forfait.'
        : detail;
      toast({ title: 'Portail indisponible', description: message, variant: 'destructive' });
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="soft-card p-6 flex items-center justify-center" data-testid="subscription-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  const status = data?.status || 'unknown';
  const plan = data?.plan;
  const usage = data?.usage || {};
  const subUntil = data?.subscription_until ? new Date(data.subscription_until) : null;
  const daysLeft = subUntil ? Math.ceil((subUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

  const statusMeta = {
    active:    { label: 'Actif',    bg: '#E6FBF7', fg: '#0F766E' },
    trial:     { label: 'Essai',    bg: '#FFF7E6', fg: '#92400E' },
    suspended: { label: 'Suspendu', bg: '#FFEAEA', fg: '#B91C1C' },
    cancelled: { label: 'Résilié',  bg: '#F3F4F8', fg: '#6B6C85' },
    unknown:   { label: status,     bg: '#F3F4F8', fg: '#6B6C85' },
  }[status] || { label: status, bg: '#F3F4F8', fg: '#6B6C85' };

  const fmtEur = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDate = (d) => d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  return (
    <div className="space-y-4" data-testid="subscription-section">
      {/* Hero card — current plan */}
      <div className="soft-card p-6" data-testid="subscription-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: 'linear-gradient(135deg, #4F46E5, #818CF8)' }}>
              <CreditCard className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[18px] font-bold text-[#1A1B2E]" data-testid="subscription-plan-name">
                  Forfait {plan?.name || plan?.code || 'inconnu'}
                </div>
                <span
                  data-testid="subscription-status-badge"
                  className="h-6 px-2.5 rounded-md text-[11px] font-bold uppercase tracking-wide flex items-center"
                  style={{ background: statusMeta.bg, color: statusMeta.fg }}
                >
                  {statusMeta.label}
                </span>
              </div>
              {plan?.description && (
                <div className="text-[13px] text-[#8E8FA5] mt-1 max-w-xl">{plan.description}</div>
              )}
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-[24px] font-bold text-[#4F46E5]">{fmtEur(plan?.price_monthly_eur)} €</span>
                <span className="text-[13px] text-[#8E8FA5]">/ mois</span>
                {plan?.price_yearly_eur ? (
                  <span className="text-[12px] text-[#8E8FA5] ml-2">· ou {fmtEur(plan.price_yearly_eur)} € /an</span>
                ) : null}
              </div>
            </div>
          </div>
          <button
            data-testid="subscription-open-portal"
            onClick={openPortal}
            disabled={opening}
            className="h-11 px-5 rounded-xl bg-[#635BFF] text-white text-[13px] font-semibold flex items-center gap-2 hover:opacity-90 disabled:opacity-60 transition-opacity"
          >
            {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Gérer via Stripe
          </button>
        </div>

        {subUntil && (
          <div className="mt-5 pt-4 border-t border-[#EFF0F6] grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
            <div>
              <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wide">Prochaine échéance</div>
              <div className="text-[14px] font-semibold text-[#1A1B2E] mt-1" data-testid="subscription-next-renewal">{fmtDate(subUntil)}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wide">Jours restants</div>
              <div className={`text-[14px] font-semibold mt-1 ${daysLeft != null && daysLeft < 7 ? 'text-[#EF4444]' : 'text-[#1A1B2E]'}`}>
                {daysLeft != null ? `${daysLeft} j` : '—'}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wide">Statut paiement</div>
              <div className="text-[14px] font-semibold text-[#1A1B2E] mt-1">{statusMeta.label}</div>
            </div>
          </div>
        )}
      </div>

      {/* Usage card */}
      <div className="soft-card p-6" data-testid="subscription-usage-card">
        <h3 className="text-[15px] font-bold text-[#1A1B2E] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Utilisation actuelle</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <UsageRow
            label="Utilisateurs"
            current={usage.users || 0}
            max={plan?.max_users}
          />
          <UsageRow
            label="Entreprises clientes"
            current={usage.companies || 0}
            max={plan?.max_companies}
          />
          <UsageRow
            label="Sessions ce mois"
            current={usage.sessions_this_month || 0}
            max={plan?.max_sessions_per_month}
          />
        </div>
      </div>

      {/* Help card */}
      <div className="soft-card p-5 bg-[#FAFAFE]" data-testid="subscription-help-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#4F46E5] shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-[#4A4B63] leading-relaxed">
            <strong>Le portail Stripe</strong> vous permet de mettre à jour votre carte bancaire, télécharger vos factures OFManager et résilier votre abonnement à tout moment, sans avoir à passer par notre équipe.
            Si le portail est indisponible (mode bac à sable ou aucun paiement enregistré), contactez votre interlocuteur OFManager.
          </div>
        </div>
      </div>
    </div>
  );
}

function UsageRow({ label, current, max }) {
  const isUnlimited = !max || max <= 0;
  const pct = isUnlimited ? 0 : Math.min(100, (current / max) * 100);
  const danger = !isUnlimited && pct >= 90;
  return (
    <div className="rounded-xl border border-[#EFF0F6] p-3.5">
      <div className="text-[11px] font-semibold text-[#8E8FA5] uppercase tracking-wide">{label}</div>
      <div className="flex items-baseline gap-1 mt-1.5">
        <span className={`text-[20px] font-bold ${danger ? 'text-[#EF4444]' : 'text-[#1A1B2E]'}`}>{current}</span>
        <span className="text-[12px] text-[#8E8FA5]">/ {isUnlimited ? '∞' : max}</span>
      </div>
      {!isUnlimited && (
        <div className="mt-2 h-1.5 rounded-full bg-[#F3F4F8] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: danger ? '#EF4444' : '#4F46E5' }}
          />
        </div>
      )}
    </div>
  );
}



function IntegrationsSection() {
  return (
    <div className="space-y-4">
      <IaIntegrationCard />
      <StripeIntegrationCard />
      <div className="soft-card p-5">
        <div className="text-[14px] font-semibold text-[#1A1B2E] mb-1">Autres intégrations</div>
        <div className="text-[12.5px] text-[#8E8FA5] mb-3">Bientôt disponibles</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: 'Resend', desc: 'Envoi automatique des emails (relances factures)', color: '#000000', connected: true },
            { name: 'Zoom', desc: 'Classes virtuelles intégrées', color: '#2D8CFF' },
            { name: 'Teams', desc: 'Réunions Microsoft Teams', color: '#6264A7' },
            { name: 'EDOF', desc: 'Export vers Mon Compte Formation', color: '#14B8A6' },
            { name: 'Sage Compta', desc: 'Synchronisation comptable', color: '#00D639' },
          ].map((i) => (
            <div key={i.name} className="border border-[#EFF0F6] rounded-xl p-3 flex items-center gap-3 opacity-70">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-[13px]" style={{ background: i.color }}>{i.name[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{i.name}</div>
                <div className="text-[11.5px] text-[#8E8FA5] truncate">{i.desc}</div>
              </div>
              <span className={`h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center ${i.connected ? 'bg-[#E6FBF7] text-[#0F766E]' : 'bg-[#F3F4F8] text-[#6B6C85]'}`}>
                {i.connected ? 'Actif' : 'Bientôt'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Gmail / Google Calendar — per-user mailbox sync — Vague 102 + 103
// =============================================================================
function GmailIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [prefs, setPrefs] = useState({ prefer_gmail_send: false, calendar_auto_sync: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pushingCalendar, setPushingCalendar] = useState(false);
  const [pubsubOpen, setPubsubOpen] = useState(false);
  const [pubsubTopic, setPubsubTopic] = useState('');
  const [registeringWatch, setRegisteringWatch] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get('/integrations/google/status'),
        api.get('/integrations/google/prefs'),
      ]);
      setStatus(a.data);
      setPrefs(b.data);
    } catch { setStatus({ connected: false }); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get('google') === 'connected') {
      toast({ title: 'Gmail connecté ✓', description: 'Vos emails et calendrier sont maintenant synchronisés.' });
      window.history.replaceState({}, '', window.location.pathname);
      load();
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
      const returnOrigin = window.location.origin;
      const { data } = await api.get('/integrations/google/authorize', { params: { return_origin: returnOrigin } });
      oauthRedirect(data.authorize_url, { onAfterPopup: () => setConnecting(false) });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Connexion impossible' });
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Gmail ? Les emails ne seront plus synchronisés.')) return;
    try {
      await api.post('/integrations/google/disconnect');
      toast({ title: 'Déconnecté', description: 'Gmail a été dissocié de votre compte.' });
      load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' }); }
  };

  const updatePref = async (key, value) => {
    const patch = { [key]: value };
    setPrefs((p) => ({ ...p, ...patch }));
    try {
      const { data } = await api.put('/integrations/google/prefs', patch);
      setPrefs(data);
      toast({ title: 'Préférence enregistrée' });
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' }); load(); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/integrations/google/sync-now');
      toast({ title: 'Synchronisation Gmail OK', description: `${data.inserted} nouveaux emails dans la timeline CRM · ${data.skipped} ignorés · ${data.scanned} scannés.` });
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sync impossible' }); }
    finally { setSyncing(false); }
  };

  const pushCalendar = async () => {
    setPushingCalendar(true);
    try {
      const { data } = await api.post('/integrations/google/calendar/push-sessions');
      toast({ title: 'Sessions poussées vers Google Calendar ✓', description: `${data.created} créées · ${data.updated} mises à jour · ${data.errors} erreurs.` });
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' }); }
    finally { setPushingCalendar(false); }
  };

  const registerWatch = async () => {
    if (!pubsubTopic.startsWith('projects/')) {
      toast({ title: 'Topic invalide', description: 'Format attendu : projects/<gcp-project>/topics/<topic>' });
      return;
    }
    setRegisteringWatch(true);
    try {
      const { data } = await api.post('/integrations/google/watch', { topic_name: pubsubTopic });
      toast({ title: 'Pub/Sub activé ✓', description: `Les emails entrants seront synchronisés en temps réel. (history_id ${data.history_id})` });
    } catch (e) { toast({ title: 'Erreur Pub/Sub', description: e?.response?.data?.detail || 'Échec' }); }
    finally { setRegisteringWatch(false); }
  };

  const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.4 2.2 30 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.2 13.2 17.6 9.5 24 9.5z" />
      <path fill="#34A853" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.6 2.9-2.2 5.3-4.7 6.9l7.3 5.7c4.3-4 6.8-9.8 6.8-17.1z" />
      <path fill="#FBBC05" d="M10.3 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.5 10.8l7.8-6.1z" />
      <path fill="#EA4335" d="M24 48c6 0 11.3-2 15.1-5.4l-7.3-5.7c-2.1 1.4-4.8 2.2-7.8 2.2-6.4 0-11.8-3.7-13.7-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );

  return (
    <div className="soft-card p-5" data-testid="gmail-integration-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white border border-[#EFF0F6]">
            <GoogleIcon />
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Satoshi','Plus Jakarta Sans',sans-serif", letterSpacing: '-0.02em' }}>
              Gmail & Google Agenda
            </h3>
            <p className="text-[12.5px] text-[#6B6C85]">Envoi d'emails via votre Gmail, sync calendrier, timeline CRM en temps réel.</p>
          </div>
        </div>
        {status?.connected && (
          <span className="h-7 px-2.5 rounded-md text-[11px] font-semibold flex items-center bg-[#E6FBF7] text-[#0F766E]">
            ✓ Connecté
          </span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12.5px] text-[#8E8FA5]">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}

      {!loading && !status?.connected && (
        <div>
          <p className="text-[12.5px] text-[#6B6C85] leading-relaxed mb-3">
            Connectez votre compte Gmail pour envoyer les emails OFManager depuis votre adresse réelle, synchroniser votre calendrier avec les sessions, et voir les conversations dans la timeline CRM.
          </p>
          <button
            onClick={connect}
            disabled={connecting}
            data-testid="gmail-connect-btn"
            className="h-10 px-4 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
            Connecter Gmail
          </button>
        </div>
      )}

      {!loading && status?.connected && (
        <div className="space-y-4">
          <div className="rounded-xl bg-[#EEF2FF] border border-[#C7D2FE] p-3">
            <div className="text-[12.5px] text-[#1A1B2E]">
              <span className="font-semibold">Adresse liée&nbsp;:</span> {status.email || '—'}
            </div>
            {status.connected_at && (
              <div className="text-[11.5px] text-[#6B6C85] mt-1">
                Connecté le {new Date(status.connected_at).toLocaleDateString('fr-FR')}
              </div>
            )}
          </div>

          {/* Prefs */}
          <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4 space-y-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4F46E5]">Préférences</div>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[#1A1B2E]">Utiliser Gmail pour envoyer les emails</div>
                <p className="text-[11.5px] text-[#6B6C85]">Relances factures, convocations, notifications — envoyés depuis <span className="font-semibold">{status.email}</span> au lieu de Resend.</p>
              </div>
              <button type="button"
                onClick={() => updatePref('prefer_gmail_send', !prefs.prefer_gmail_send)}
                data-testid="pref-prefer-gmail-send"
                className={`w-11 h-6 rounded-full relative shrink-0 transition-colors ${prefs.prefer_gmail_send ? 'bg-[#4F46E5]' : 'bg-[#D1D5E0]'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${prefs.prefer_gmail_send ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[#1A1B2E]">Synchroniser automatiquement les sessions → Google Calendar</div>
                <p className="text-[11.5px] text-[#6B6C85]">Chaque création/mise à jour de session crée ou met à jour un événement dans votre Google Agenda.</p>
              </div>
              <button type="button"
                onClick={() => updatePref('calendar_auto_sync', !prefs.calendar_auto_sync)}
                data-testid="pref-calendar-auto-sync"
                className={`w-11 h-6 rounded-full relative shrink-0 transition-colors ${prefs.calendar_auto_sync ? 'bg-[#4F46E5]' : 'bg-[#D1D5E0]'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${prefs.calendar_auto_sync ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={syncNow} disabled={syncing}
              data-testid="gmail-sync-now-btn"
              className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#4338CA] transition-colors disabled:opacity-60"
            >
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Synchroniser les emails maintenant
            </button>
            <button
              onClick={pushCalendar} disabled={pushingCalendar}
              data-testid="calendar-push-sessions-btn"
              className="h-9 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[#1A1B2E] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60"
            >
              {pushingCalendar ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarDays className="w-3.5 h-3.5" />}
              Pousser toutes les sessions vers Google Calendar
            </button>
            <button
              onClick={disconnect}
              data-testid="gmail-disconnect-btn"
              className="h-9 px-3 rounded-lg border border-[#FCA5A5] text-[#B91C1C] bg-white text-[12.5px] font-semibold hover:bg-[#FEF2F2] transition-colors"
            >
              Déconnecter
            </button>
          </div>

          {/* Advanced: Pub/Sub */}
          <div className="rounded-xl border border-[#EFF0F6] bg-white p-4">
            <button
              onClick={() => setPubsubOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left"
              data-testid="pubsub-accordion-toggle"
            >
              <div>
                <div className="text-[13px] font-semibold text-[#1A1B2E]">Notifications en temps réel (Gmail Pub/Sub)</div>
                <p className="text-[11.5px] text-[#6B6C85]">Optionnel — Requiert un topic Google Cloud Pub/Sub côté GCP.</p>
              </div>
              <span className="text-[#8E8FA5] text-[16px]">{pubsubOpen ? '−' : '+'}</span>
            </button>
            {pubsubOpen && (
              <div className="mt-3 pt-3 border-t border-[#EFF0F6] space-y-3">
                <ol className="text-[11.5px] text-[#6B6C85] space-y-1 list-decimal pl-4">
                  <li>Créez un topic Pub/Sub dans Google Cloud Console (ex. <code>gmail-notifications</code>).</li>
                  <li>Donnez le rôle <strong>Publisher</strong> au compte de service <code>gmail-api-push@system.gserviceaccount.com</code> sur ce topic.</li>
                  <li>Créez un subscription <strong>Push</strong> pointant vers <code className="break-all">{window.location.origin}/api/integrations/google/pubsub</code>.</li>
                  <li>Collez le nom complet du topic ci-dessous puis cliquez sur <strong>Activer</strong>.</li>
                </ol>
                <input
                  type="text"
                  value={pubsubTopic}
                  onChange={(e) => setPubsubTopic(e.target.value)}
                  placeholder="projects/mon-projet/topics/gmail-notifications"
                  data-testid="pubsub-topic-input"
                  className="w-full h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] font-mono outline-none focus:bg-white focus:border-[#4F46E5]"
                />
                <button
                  onClick={registerWatch}
                  disabled={registeringWatch || !pubsubTopic.trim()}
                  data-testid="pubsub-activate-btn"
                  className="h-9 px-3 rounded-lg bg-[#4F46E5] text-white text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-60"
                >
                  {registeringWatch ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  Activer Pub/Sub
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// Stripe — per-tenant credentials (test / live) — Vague 98
// =============================================================================
function StripeIntegrationCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null); // public view from backend
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    test_sk: '', test_pk: '', test_whsec: '',
    live_sk: '', live_pk: '', live_whsec: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/integrations/stripe');
      setData(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Impossible de charger la configuration Stripe';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const switchMode = async (mode) => {
    if (data?.mode === mode) return;
    try {
      const { data: newData } = await api.post('/integrations/stripe/switch-mode', { mode });
      setData(newData);
      toast({ title: 'Mode mis à jour', description: `Stripe : ${mode === 'live' ? 'Production' : 'Test'}` });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Bascule impossible';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Send only fields the user actually changed (non-empty strings).
      const payload = {};
      ['test_sk', 'test_pk', 'test_whsec', 'live_sk', 'live_pk', 'live_whsec'].forEach((k) => {
        if (form[k]) payload[k] = form[k].trim();
      });
      if (Object.keys(payload).length === 0) {
        toast({ title: 'Rien à enregistrer', description: 'Saisissez au moins une clé.' });
        setSaving(false);
        return;
      }
      const { data: newData } = await api.put('/integrations/stripe', payload);
      setData(newData);
      setShowForm(false);
      setForm({ test_sk: '', test_pk: '', test_whsec: '', live_sk: '', live_pk: '', live_whsec: '' });
      toast({ title: 'Connexion Stripe enregistrée', description: 'Vos clés ont été validées et chiffrées.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Échec de la validation des clés';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Déconnecter complètement Stripe ? Toutes les clés (test + production) seront supprimées.")) return;
    try {
      await api.delete('/integrations/stripe');
      await fetchData();
      toast({ title: 'Stripe déconnecté' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Échec';
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="soft-card p-6 flex items-center justify-center" data-testid="stripe-integration-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" />
      </div>
    );
  }

  const mode = data?.mode || 'test';
  const active = data?.[mode] || {};
  const isConnected = !!active.configured;
  const otherEnv = mode === 'test' ? 'live' : 'test';

  return (
    <div className="soft-card p-5" data-testid="stripe-integration-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0" style={{ background: '#635BFF' }}>S</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[15px] font-semibold text-[#1A1B2E]">Stripe</div>
            <span
              data-testid="stripe-mode-badge"
              className={`h-5 px-2 rounded-md text-[10.5px] font-bold uppercase tracking-wide flex items-center ${
                mode === 'live' ? 'bg-[#FFEAEA] text-[#B91C1C]' : 'bg-[#FFF7E6] text-[#92400E]'
              }`}
            >
              {mode === 'live' ? 'Production' : 'Test'}
            </span>
            {isConnected ? (
              <span className="h-5 px-2 rounded-md text-[10.5px] font-semibold bg-[#E6FBF7] text-[#0F766E] flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Connecté
              </span>
            ) : (
              <span className="h-5 px-2 rounded-md text-[10.5px] font-semibold bg-[#F3F4F8] text-[#6B6C85] flex items-center">
                Non connecté
              </span>
            )}
          </div>
          <div className="text-[12.5px] text-[#8E8FA5] mt-0.5">
            Connectez votre compte Stripe pour encaisser les paiements de vos factures et donner accès au portail client à vos stagiaires/entreprises.
          </div>

          {isConnected && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12.5px]">
              <InfoRow label="Compte" value={active.account_name || active.account_id || '—'} />
              <InfoRow label="ID compte" value={active.account_id || '—'} mono />
              <InfoRow label="Clé secrète" value={active.sk_masked || '—'} mono />
              <InfoRow label="Clé publique" value={active.pk || '—'} mono />
              <InfoRow label="Webhook secret" value={active.whsec_masked || '—'} mono />
              <InfoRow
                label="Paiements activés"
                value={active.charges_enabled ? '✅ Oui' : '⚠️ Non (compte Stripe incomplet)'}
              />
            </div>
          )}

          {data?.webhook_url && (
            <div className="mt-4 p-3 rounded-xl bg-[#F4F2FF] border border-[#D6D3FF]" data-testid="stripe-webhook-block">
              <div className="text-[11.5px] font-semibold text-[#635BFF] uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> Webhook Stripe — Encaissement automatique
                {(data?.test?.whsec_masked || data?.live?.whsec_masked) ? (
                  <span className="ml-auto h-4 px-1.5 rounded text-[9.5px] font-bold bg-[#E6FBF7] text-[#0F766E]">Actif</span>
                ) : (
                  <span className="ml-auto h-4 px-1.5 rounded text-[9.5px] font-bold bg-[#FEF3E7] text-[#92400E]">À configurer</span>
                )}
              </div>
              <div className="text-[11.5px] text-[#4A4B63] mb-2 leading-relaxed">
                Copiez cette URL dans <strong>Dashboard Stripe → Développeurs → Webhooks → Ajouter un endpoint</strong>, puis abonnez-vous à <code className="px-1 py-0.5 rounded bg-white border border-[#D6D3FF] text-[10.5px]">checkout.session.completed</code> et <code className="px-1 py-0.5 rounded bg-white border border-[#D6D3FF] text-[10.5px]">payment_intent.succeeded</code>. Stripe vous donnera un <code>whsec_…</code> à coller dans le champ « Webhook secret » ci-dessus. Vos factures basculeront alors en <strong>Payée</strong> automatiquement dès qu'un client règle.
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="stripe-webhook-url"
                  readOnly
                  value={data.webhook_url}
                  onClick={(e) => e.target.select()}
                  className="flex-1 h-8 px-2.5 rounded-md bg-white border border-[#D6D3FF] text-[11.5px] font-mono text-[#1A1B2E]"
                />
                <button
                  data-testid="stripe-webhook-copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(data.webhook_url);
                      toast({ title: 'URL copiée', description: data.webhook_url });
                    } catch {
                      toast({ title: 'Copie impossible' });
                    }
                  }}
                  className="h-8 px-2.5 rounded-md bg-white border border-[#D6D3FF] text-[#635BFF] text-[11px] font-semibold inline-flex items-center gap-1 hover:bg-[#EDE8FF]"
                >
                  Copier
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="stripe-toggle-form"
              onClick={() => setShowForm((v) => !v)}
              className="h-9 px-3.5 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
            >
              <Edit3 className="w-3.5 h-3.5" /> {isConnected ? 'Modifier les clés' : 'Connecter Stripe'}
            </button>
            {data?.[otherEnv]?.configured && (
              <button
                data-testid="stripe-switch-mode"
                onClick={() => switchMode(otherEnv)}
                className="h-9 px-3.5 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold inline-flex items-center gap-1.5"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Basculer en {otherEnv === 'live' ? 'Production' : 'Test'}
              </button>
            )}
            {(data?.test?.configured || data?.live?.configured) && (
              <button
                data-testid="stripe-disconnect"
                onClick={onDisconnect}
                className="h-9 px-3.5 rounded-lg bg-[#FEF2F2] text-[#B91C1C] text-[12.5px] font-semibold inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Déconnecter
              </button>
            )}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noreferrer"
              className="h-9 px-3.5 rounded-lg bg-white border border-[#EFF0F6] text-[#4A4B63] text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F6F7FB]"
            >
              Récupérer mes clés Stripe →
            </a>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="mt-5 pt-5 border-t border-[#EFF0F6] space-y-4" data-testid="stripe-form">
          {/* Vague 99.1 — Mini-tutoriel cliquable pour récupérer les 3 clés Stripe */}
          <StripeOnboardingTutorial webhookUrl={data?.webhook_url} />

          <div className="text-[12.5px] text-[#4A4B63] bg-[#EEF2FF] border border-[#E0DAFF] rounded-lg p-3">
            <strong>Comment ça marche ?</strong> Renseignez vos clés Stripe (Test pour vos essais, Production pour facturer réellement vos clients).
            Les clés secrètes sont chiffrées avant stockage. Nous validons votre clé en appelant Stripe avant d'enregistrer.
          </div>
          <StripeKeysGroup
            envLabel="Mode Test"
            envHint="Clés sk_test_… / pk_test_… — utilisez ces clés pour vos essais sans vrais débits."
            sk={form.test_sk} pk={form.test_pk} whsec={form.test_whsec}
            onChange={(patch) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [`test_${k}`, v])) }))}
            existing={data?.test}
          />
          <StripeKeysGroup
            envLabel="Mode Production"
            envHint="Clés sk_live_… / pk_live_… — vrais paiements, vraies entreprises."
            sk={form.live_sk} pk={form.live_pk} whsec={form.live_whsec}
            onChange={(patch) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(patch).map(([k, v]) => [`live_${k}`, v])) }))}
            existing={data?.live}
          />
          <div className="flex gap-2 justify-end">
            <button
              data-testid="stripe-cancel"
              onClick={() => { setShowForm(false); setForm({ test_sk: '', test_pk: '', test_whsec: '', live_sk: '', live_pk: '', live_whsec: '' }); }}
              className="h-9 px-3.5 rounded-lg bg-[#F3F4F8] text-[#4A4B63] text-[12.5px] font-semibold"
            >
              Annuler
            </button>
            <button
              data-testid="stripe-save"
              onClick={onSave}
              disabled={saving}
              className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Validation Stripe…</> : <><Save className="w-3.5 h-3.5" /> Vérifier & Enregistrer</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StripeOnboardingTutorial({ webhookUrl }) {
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState(() => {
    try {
      // SAFE: stores ONLY non-sensitive UI state — array of step keys the
      // user has marked as completed (used to keep the tutorial collapsed
      // once finished). No credentials.
      return JSON.parse(localStorage.getItem('ifaap_stripe_tutorial_done') || '[]');
    } catch (e) { console.warn("[silent]", e); return []; }
  });
  const toggleStep = (k) => {
    setDone((prev) => {
      const next = prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k];
      try { localStorage.setItem('ifaap_stripe_tutorial_done', JSON.stringify(next)); } catch (e) { console.warn("[silent]", e); }
      return next;
    });
  };
  const allDone = ['pk', 'sk', 'whsec'].every((k) => done.includes(k));
  const copyWebhook = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast({ title: 'URL copiée', description: webhookUrl });
    } catch (e) {
      toast({ title: 'Copie impossible' });
    }
  };

  return (
    <div className="rounded-xl border-2 border-[#635BFF] bg-gradient-to-br from-[#F4F2FF] to-white overflow-hidden" data-testid="stripe-tutorial">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="stripe-tutorial-toggle"
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[#EDE8FF]/50"
      >
        <div className="w-8 h-8 rounded-lg bg-[#635BFF] text-white font-bold flex items-center justify-center text-[13px] shrink-0">?</div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-[#1A1B2E] flex items-center gap-2">
            Récupérer vos clés Stripe — guide pas-à-pas
            {allDone && <span className="h-5 px-2 rounded text-[10px] font-bold bg-[#0F766E] text-white">Toutes les étapes faites ✓</span>}
          </div>
          <div className="text-[11.5px] text-[#6B6C85]">Cliquez pour {open ? 'masquer' : 'afficher'} les 3 étapes (≈ 3 min).</div>
        </div>
        <span className="text-[#635BFF] text-[12px] font-bold">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2.5 text-[12px] text-[#1A1B2E]">
          <TutoStep
            n={1}
            keyId="pk"
            title="Clé publique (pk_live_…)"
            done={done.includes('pk')}
            onToggle={() => toggleStep('pk')}
            data-testid="stripe-tutorial-step-pk"
          >
            <p className="text-[11.5px] text-[#4A4B63]">
              Sur Stripe : <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#635BFF] font-semibold underline">Développeurs → Clés API</a> → bloc <strong>« Clés standard »</strong> → ligne <strong>« Clé publique »</strong> → cliquez pour copier (commence par <code className="bg-white px-1 rounded">pk_live_</code>).
            </p>
          </TutoStep>

          <TutoStep
            n={2}
            keyId="sk"
            title="Clé secrète (sk_live_…)"
            done={done.includes('sk')}
            onToggle={() => toggleStep('sk')}
            data-testid="stripe-tutorial-step-sk"
          >
            <p className="text-[11.5px] text-[#4A4B63]">
              Sur la même page Stripe → ligne <strong>« Clé secrète »</strong> (affichée masquée <code className="bg-white px-1 rounded">sk_live_…wsd6</code>) → cliquez sur les <strong>«&nbsp;⋯&nbsp;»</strong> à droite → <strong>« Révéler la clé en direct »</strong> (mot de passe Stripe demandé) → copiez la clé complète (~107 caractères).
            </p>
            <div className="text-[10.5px] text-[#B91C1C] mt-1">⚠ Cette clé donne accès à tout votre compte Stripe — elle est chiffrée avant stockage chez OFManager.</div>
          </TutoStep>

          <TutoStep
            n={3}
            keyId="whsec"
            title="Webhook secret (whsec_…)"
            done={done.includes('whsec')}
            onToggle={() => toggleStep('whsec')}
            data-testid="stripe-tutorial-step-whsec"
          >
            <p className="text-[11.5px] text-[#4A4B63] mb-2">
              C'est <strong>l'étape la plus oubliée</strong>. Sans webhook, vos factures ne basculeront pas automatiquement en « Payée ».
            </p>
            <ol className="text-[11.5px] text-[#4A4B63] list-decimal pl-5 space-y-1">
              <li>Allez sur <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-[#635BFF] font-semibold underline">Développeurs → Webhooks</a></li>
              <li>Cliquez <strong>« + Ajouter un endpoint »</strong></li>
              <li>
                <strong>URL du endpoint</strong> : copiez l'URL ci-dessous
                {webhookUrl ? (
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 px-2 py-1 rounded bg-white border border-[#D6D3FF] text-[10.5px] font-mono break-all">{webhookUrl}</code>
                    <button
                      type="button"
                      onClick={copyWebhook}
                      className="h-7 px-2 rounded bg-[#635BFF] text-white text-[10.5px] font-semibold hover:bg-[#4F47E5] shrink-0"
                      data-testid="stripe-tutorial-copy-webhook"
                    >
                      Copier
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 text-[10.5px] text-[#92400E]">L'URL apparaîtra ici après votre 1ère sauvegarde.</div>
                )}
              </li>
              <li>
                <strong>Événements à écouter</strong> : cochez UNIQUEMENT ces 2 :
                <div className="mt-1 flex flex-wrap gap-1">
                  <code className="px-1.5 py-0.5 rounded bg-white border border-[#D6D3FF] text-[10.5px]">checkout.session.completed</code>
                  <code className="px-1.5 py-0.5 rounded bg-white border border-[#D6D3FF] text-[10.5px]">payment_intent.succeeded</code>
                </div>
              </li>
              <li>Validez avec <strong>« Ajouter un endpoint »</strong></li>
              <li>Sur la page suivante → bloc <strong>« Secret de signature »</strong> → cliquez <strong>« Révéler »</strong> → copiez le <code className="bg-white px-1 rounded">whsec_…</code></li>
            </ol>
          </TutoStep>

          <div className="text-[11px] text-[#4A4B63] bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2 mt-3">
            <strong>Astuce :</strong> en mode Test, refaites la même procédure sur <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noreferrer" className="text-[#635BFF] font-semibold underline">/test/apikeys</a> et <a href="https://dashboard.stripe.com/test/webhooks" target="_blank" rel="noreferrer" className="text-[#635BFF] font-semibold underline">/test/webhooks</a> — clés <code className="bg-white px-1 rounded">pk_test_</code> / <code className="bg-white px-1 rounded">sk_test_</code> / un autre <code className="bg-white px-1 rounded">whsec_</code>.
          </div>
        </div>
      )}
    </div>
  );
}

function TutoStep({ n, title, done, onToggle, children, ...rest }) {
  return (
    <div
      className={`rounded-lg p-3 border ${done ? 'bg-[#F0FDF4] border-[#86EFAC]' : 'bg-white border-[#EFF0F6]'}`}
      {...rest}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-2 text-left"
      >
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${done ? 'bg-[#0F766E] text-white' : 'bg-[#EEF2FF] text-[#635BFF]'}`}>
          {done ? '✓' : n}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[12.5px] font-bold ${done ? 'text-[#0F766E] line-through' : 'text-[#1A1B2E]'}`}>{title}</div>
          {!done && <div className="mt-1.5">{children}</div>}
        </div>
      </button>
    </div>
  );
}


function InfoRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[#8E8FA5] shrink-0">{label} :</span>
      <span className={`text-[#1A1B2E] font-medium truncate ${mono ? 'font-mono text-[11.5px]' : ''}`} title={value}>{value}</span>
    </div>
  );
}

function StripeKeysGroup({ envLabel, envHint, sk, pk, whsec, onChange, existing }) {
  return (
    <div className="border border-[#EFF0F6] rounded-xl p-4 bg-[#FAFAFC]">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[13px] font-semibold text-[#1A1B2E]">{envLabel}</div>
        {existing?.configured && (
          <span className="text-[10.5px] font-semibold bg-[#E6FBF7] text-[#0F766E] px-2 py-0.5 rounded">
            Déjà configuré
          </span>
        )}
      </div>
      <div className="text-[11.5px] text-[#8E8FA5] mb-3">{envHint}</div>
      <div className="space-y-2.5">
        <KeyField
          label="Clé secrète (sk_…)"
          value={sk}
          onChange={(v) => onChange({ sk: v })}
          placeholder={existing?.sk_masked ? `Actuel : ${existing.sk_masked} — laissez vide pour ne pas changer` : 'sk_test_… ou sk_live_…'}
          masked
        />
        <KeyField
          label="Clé publique (pk_…)"
          value={pk}
          onChange={(v) => onChange({ pk: v })}
          placeholder={existing?.pk ? `Actuel : ${existing.pk} — laissez vide pour ne pas changer` : 'pk_test_… ou pk_live_…'}
        />
        <KeyField
          label="Webhook secret (whsec_…) — facultatif"
          value={whsec}
          onChange={(v) => onChange({ whsec: v })}
          placeholder={existing?.whsec_masked ? `Actuel : ${existing.whsec_masked} — laissez vide pour ne pas changer` : 'whsec_… (à créer dans Dashboard Stripe → Webhooks)'}
          masked
        />
      </div>
    </div>
  );
}

function KeyField({ label, value, onChange, placeholder, masked = false }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-[11.5px] font-semibold text-[#4A4B63] block mb-1">{label}</label>
      <div className="relative">
        <input
          type={masked && !show ? 'password' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck="false"
          className="w-full h-9 pl-3 pr-16 rounded-lg bg-white border border-[#EFF0F6] text-[12.5px] font-mono focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
        />
        {masked && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10.5px] font-semibold text-[#4F46E5] hover:underline"
          >
            {show ? 'Masquer' : 'Afficher'}
          </button>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// Modèles de documents — Livret d'accueil + Questionnaires
// =============================================================================
function DocsModelsSection() {
  return (
    <div className="space-y-4">
      <div className="soft-card p-5">
        <h2 className="text-[16px] font-semibold mb-1 text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Modèles de documents</h2>
        <p className="text-[12.5px] text-[#6B6C85]">Configurez les documents partagés joints automatiquement aux convocations et envois clients.</p>
      </div>
      <WelcomeBookletCard />
      <QuestionnaireBuilderCard />
    </div>
  );
}

function WelcomeBookletCard() {
  const [meta, setMeta] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const inputRef = React.useRef(null);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get('/orgs/me');
      setMeta({
        filename: data.welcome_booklet_filename,
        size: data.welcome_booklet_size,
        uploaded_at: data.welcome_booklet_uploaded_at,
      });
    } catch (e) { console.warn("[silent]", e); }
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  const onSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && !file.type.includes('pdf')) {
      setError('Le fichier doit être un PDF.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Fichier > 8 Mo.');
      e.target.value = '';
      return;
    }
    setError('');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/org-documents/welcome-booklet', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refresh();
      toast({ title: 'Livret d\'accueil mis à jour' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload impossible.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const onDelete = async () => {
    if (!window.confirm('Supprimer le livret d\'accueil ?')) return;
    try {
      await api.delete('/org-documents/welcome-booklet');
      await refresh();
      toast({ title: 'Livret supprimé' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Suppression impossible.');
    }
  };
  const onView = async () => {
    try {
      const res = await api.get('/org-documents/welcome-booklet', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('Impossible d\'ouvrir le PDF.');
    }
  };

  const has = !!meta?.filename;
  return (
    <div className="soft-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#E6FBF7] text-[#0F766E] flex items-center justify-center"><FileText className="w-4 h-4" /></div>
        <div>
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]">Livret d'accueil</h3>
          <p className="text-[11.5px] text-[#6B6C85]">Joint automatiquement à chaque convocation envoyée aux apprenants.</p>
        </div>
      </div>
      {has ? (
        <div className="flex items-center gap-2 rounded-xl border border-[#A7F3D0] bg-[#E6FBF7] px-3 py-2.5">
          <FileText className="w-4 h-4 text-[#0F766E] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#0F766E] truncate">{meta.filename}</div>
            <div className="text-[11px] text-[#0F766E]/80">{Math.round((meta.size || 0) / 1024)} Ko · uploadé le {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleDateString('fr-FR') : '—'}</div>
          </div>
          <button data-testid="welcome-booklet-view" onClick={onView} className="text-[12px] font-semibold text-[#4F46E5] hover:underline">Voir</button>
          <button data-testid="welcome-booklet-replace" onClick={() => inputRef.current?.click()} disabled={busy} className="text-[12px] font-semibold text-[#4F46E5] hover:underline disabled:opacity-60">{busy ? 'Upload…' : 'Remplacer'}</button>
          <button data-testid="welcome-booklet-delete" onClick={onDelete} className="w-7 h-7 rounded-lg hover:bg-white text-[#EF4444] flex items-center justify-center" title="Supprimer">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button data-testid="welcome-booklet-upload" onClick={() => inputRef.current?.click()} disabled={busy}
          className="w-full h-11 px-3 rounded-xl bg-[#EEF2FF] hover:bg-[#EDE8FF] border border-dashed border-[#C7B7FA] text-[13px] font-semibold text-[#4F46E5] flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          {busy ? 'Upload…' : 'Uploader le livret d\'accueil (PDF)'}
        </button>
      )}
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onSelect} data-testid="welcome-booklet-input" />
      {error && <div className="mt-2 text-[11.5px] text-[#B91C1C]">{error}</div>}
      <div className="mt-2 text-[11px] text-[#8E8FA5]">PDF — 8 Mo max.</div>
    </div>
  );
}




// =============================================================================
// QuestionnaireBuilderCard — création de questionnaires en ligne (Vague 82)
// =============================================================================
const QTYPE_LABEL = { text: 'Texte court', long_text: 'Texte long', single: 'Choix unique', multi: 'Choix multiple', scale: 'Échelle (note)' };
// Les étapes viennent du référentiel : c'est lui qui dit à qui chacune part
// et quand, et deux listes finiraient par ne pas dire la même chose. Le repli
// ci-dessous ne sert qu'au premier rendu, avant que la réponse n'arrive.
const QKIND_REPLI = {
  needs_analysis: 'Étude de besoin',
  positioning: 'Questionnaire de positionnement',
  evaluation: 'Validation des acquis',
  satisfaction: 'Satisfaction apprenant',
  satisfaction_trainer: 'Satisfaction formateur',
  satisfaction_company: 'Satisfaction entreprise',
  satisfaction_funder: 'Satisfaction financeur',
  other: 'Autre',
};

// Le chiffre de tete d'une option est sa note : « 1 - Très insatisfait ».
// La même règle vit au serveur, qui fait foi ; ici elle ne décide que de
// l'affichage d'un champ.
const noteDeLOption = (t) => {
  const m = /^\s*(\d{1,2})\s*[-–—.)\/:]\s*\S/.exec(String(t || ''));
  return m ? Number(m[1]) : null;
};
const questionNotee = (q) => {
  if (q?.type === 'scale') return true;
  if (q?.type !== 'single') return false;
  const n = (q.options || []).map(noteDeLOption).filter((x) => x !== null);
  return n.length >= 3 && Math.max(...n) > Math.min(...n);
};

function useTypesQuestionnaire() {
  const [types, setTypes] = useState(null);
  useEffect(() => {
    let vivant = true;
    api.get('/types-questionnaire')
      .then(({ data }) => { if (vivant) setTypes(data.types || []); })
      .catch(() => { if (vivant) setTypes([]); });
    return () => { vivant = false; };
  }, []);
  const libelle = (code) => (types || []).find((t) => t.code === code)?.libelle
    || QKIND_REPLI[code] || code;
  return { types, libelle };
}

function QuestionnaireBuilderCard() {
  const { libelle: libelleType } = useTypesQuestionnaire();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | { id?, title, kind, description, questions }
  const [stats, setStats] = useState(null); // { qid, total, stats[], questionnaire }

  const refresh = React.useCallback(async () => {
    try { const { data } = await api.get('/questionnaires-builder'); setItems(data || []); } catch (e) { console.warn("[silent]", e); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const newOne = () => setEditing({ title: '', kind: 'positioning', description: '', questions: [] });
  const remove = async (qid) => {
    if (!window.confirm('Supprimer ce questionnaire ? Les invitations existantes resteront actives.')) return;
    try { await api.delete(`/questionnaires-builder/${qid}`); await refresh(); toast({ title: 'Questionnaire supprimé' }); }
    catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Suppression impossible' }); }
  };
  const openStats = async (qid) => {
    try {
      const { data } = await api.get(`/questionnaires-builder/${qid}/stats`);
      setStats({ qid, ...data });
    } catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Stats indisponibles' }); }
  };

  return (
    <div className="soft-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"><FileText className="w-4 h-4" /></div>
        <div className="flex-1">
          <h3 className="text-[14px] font-semibold text-[#1A1B2E]">Questionnaires en ligne (Qualiopi)</h3>
          <p className="text-[11.5px] text-[#6B6C85]">Créez vos questionnaires directement dans OFManager. Envoi automatique J-1 (positionnement) et J+1 (satisfaction).</p>
        </div>
        <button data-testid="qbuilder-new" onClick={newOne} className="h-9 px-3.5 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95">
          <Plus className="w-3.5 h-3.5" /> Nouveau
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-6 text-center text-[12.5px] text-[#8E8FA5]">
          Aucun questionnaire en ligne pour l'instant. Cliquez sur « Nouveau » pour commencer.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((q) => (
            <div key={q.id} data-testid={`qbuilder-row-${q.id}`} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#C7B7FA] transition-colors">
              <span className="px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[10.5px] font-semibold uppercase tracking-wide whitespace-nowrap">{libelleType(q.kind)}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{q.title}</div>
                <div className="text-[11px] text-[#6B6C85]">
                  {(q.questions || []).length} question(s) · maj {q.updated_at ? new Date(q.updated_at).toLocaleDateString('fr-FR') : '—'}
                  {(q.programme_ids || []).length > 0
                    ? <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] text-[10px] font-semibold">{q.programme_ids.length} programme(s)</span>
                    : <span className="ml-1.5 px-1.5 py-0.5 rounded bg-[#F3F4F8] text-[#6B6C85] text-[10px] font-semibold">défaut org</span>
                  }
                </div>
              </div>
              <button data-testid={`qbuilder-stats-${q.id}`} onClick={() => openStats(q.id)} className="text-[11.5px] font-semibold text-[#0F766E] hover:underline">Stats</button>
              <button data-testid={`qbuilder-edit-${q.id}`} onClick={() => setEditing(q)} className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline">Éditer</button>
              <button data-testid={`qbuilder-delete-${q.id}`} onClick={() => remove(q.id)} className="w-7 h-7 rounded-lg hover:bg-white text-[#EF4444] flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <QuestionnaireBuilderEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); }}
        />
      )}
      {stats && <QuestionnaireStatsModal data={stats} onClose={() => setStats(null)} />}
    </div>
  );
}

// **Le seuil que le serveur applique quand personne n'en saisit un** — et il ne
// vaut que sur une evaluation : un questionnaire de positionnement sert a
// placer quelqu'un, pas a le valider. La verite vit dans `routes/acquis.py`
// (`NOTE_DE_PASSAGE`), lue par `qcm_correction.note_appliquee` ; ce chiffre-ci
// n'est qu'un libelle, il ne decide de rien. Il est ecrit la pour que l'ecran
// cesse de dire « rien n'est tranche » sur un champ vide qui tranche.
const SEUIL_PAR_DEFAUT_EVALUATION = 70;

function QuestionnaireBuilderEditor({ initial, onClose, onSaved }) {
  const [title, setTitle] = useState(initial.title || '');
  const [kind, setKind] = useState(initial.kind || 'positioning');
  const [description, setDescription] = useState(initial.description || '');
  const [questions, setQuestions] = useState(initial.questions || []);
  const [noteDePassage, setNoteDePassage] = useState(
    initial.note_de_passage === null || initial.note_de_passage === undefined
      ? '' : String(initial.note_de_passage));
  const [seuilAlerte, setSeuilAlerte] = useState(
    initial.seuil_alerte === null || initial.seuil_alerte === undefined
      ? '' : String(initial.seuil_alerte));
  const [programmeIds, setProgrammeIds] = useState(initial.programme_ids || []);
  const { types: typesQ, libelle: libelleType } = useTypesQuestionnaire();
  // Une question compte si elle déclare ses bonnes réponses, ou si quelqu'un
  // doit la lire. C'est la même règle que côté serveur.
  const nbNotees = questions.filter(
    (q) => (['single', 'multi'].includes(q.type) && (q.bonnes_reponses || []).length)
      || q.a_corriger).length;
  const nbEtoiles = questions.filter(questionNotee).length;
  const [programs, setPrograms] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/programs').then(({ data }) => setPrograms(data || [])).catch(() => {});
  }, []);

  const addQuestion = () => {
    const id = `q${Date.now().toString(36)}`;
    setQuestions((q) => [...q, { id, type: 'text', label: '', required: false, options: [], scale_min: 1, scale_max: 5 }]);
  };
  const updateQ = (i, patch) => setQuestions((arr) => arr.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQ = (i) => setQuestions((arr) => arr.filter((_, idx) => idx !== i));
  const moveQ = (i, dir) => {
    setQuestions((arr) => {
      const next = [...arr];
      const j = i + dir;
      if (j < 0 || j >= next.length) return next;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const toggleProgramme = (pid) => setProgrammeIds((arr) => arr.includes(pid) ? arr.filter((x) => x !== pid) : [...arr, pid]);

  const save = async () => {
    if (!title.trim()) { toast({ title: 'Titre requis' }); return; }
    if (questions.length === 0) { toast({ title: 'Ajoutez au moins une question' }); return; }
    for (const q of questions) {
      if (!q.label?.trim()) { toast({ title: 'Toutes les questions doivent avoir un libellé' }); return; }
      if ((q.type === 'single' || q.type === 'multi') && (!q.options || q.options.length === 0)) {
        toast({ title: `Ajoutez des options à : "${q.label}"` }); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        title, kind, description, questions, programme_ids: programmeIds,
        note_de_passage: noteDePassage === '' ? null : Number(noteDePassage),
        seuil_alerte: seuilAlerte === '' ? null : Number(seuilAlerte),
      };
      if (initial.id) await api.put(`/questionnaires-builder/${initial.id}`, payload);
      else await api.post('/questionnaires-builder', payload);
      toast({ title: 'Questionnaire enregistré' });
      await onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Enregistrement impossible' });
    }
    setBusy(false);
  };

  return createPortal((
    <div className="fixed inset-0 z-[100] bg-[#1A1B2E]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="qbuilder-editor">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between shrink-0 bg-white">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1A1B2E]">{initial.id ? 'Modifier le questionnaire' : 'Nouveau questionnaire'}</h3>
            <p className="text-[11.5px] text-[#6B6C85] mt-0.5">{questions.length} question(s) · {programmeIds.length === 0 ? 'défaut org' : `${programmeIds.length} programme(s) rattaché(s)`}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F6F7FB] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Titre</label>
              <input data-testid="qbuilder-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Positionnement Excel niveau 1" className="w-full h-10 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px] focus:border-[#4F46E5] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Type</label>
              <select data-testid="qbuilder-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13.5px]">
                {(typesQ || Object.entries(QKIND_REPLI).map(([code, libelle]) => ({ code, libelle })))
                  .map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Description (optionnelle)</label>
            <textarea data-testid="qbuilder-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none resize-none" placeholder="Instructions pour les apprenants" />
          </div>
          {nbNotees > 0 && (
            <div className="rounded-xl border border-[#C7D2FE] bg-[#FAFAFE] p-3"
                 data-testid="qbuilder-note-de-passage">
              <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
                Note de passage
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="0" max="100" value={noteDePassage}
                  onChange={(e) => setNoteDePassage(e.target.value)}
                  placeholder={kind === 'evaluation' ? String(SEUIL_PAR_DEFAUT_EVALUATION) : '—'}
                  className="w-24 h-9 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px]"
                />
                <span className="text-[12.5px] text-[#4A4B63]">%</span>
                {/* **L'ecran disait « rien n'est tranche » sur un champ qui
                    tranche** (2026-11-02). Depuis que les evaluations ont un
                    seuil par defaut, un champ vide applique 70 % — et le
                    laisser annoncer le contraire, c'etait promettre l'inverse
                    de ce que le serveur fait. Ailleurs la phrase reste vraie :
                    sans seuil saisi, un positionnement ne conclut rien. */}
                <span className="text-[11.5px] text-[#8E8FA5]">
                  {nbNotees} question(s) notée(s).{' '}
                  {kind === 'evaluation' ? (
                    <>
                      Laissée vide, le seuil de la maison s&rsquo;applique :{' '}
                      <strong className="text-[#4A4B63]">{SEUIL_PAR_DEFAUT_EVALUATION} %</strong>.
                    </>
                  ) : (
                    <>Laissée vide, la copie est notée mais rien n&rsquo;est tranché.</>
                  )}
                </span>
              </div>
            </div>
          )}
          {nbEtoiles > 0 && (
            <div className="rounded-xl border border-[#FCD9A6] bg-[#FEF9F2] p-3"
                 data-testid="qbuilder-seuil-alerte">
              <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
                Seuil d&rsquo;alerte
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min="1" max="5" step="0.5" value={seuilAlerte}
                  onChange={(e) => setSeuilAlerte(e.target.value)}
                  placeholder="3"
                  className="w-24 h-9 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px]"
                />
                <span className="text-[12.5px] text-[#4A4B63]">/ 5</span>
                <span className="text-[11.5px] text-[#8E8FA5]">
                  {nbEtoiles} question(s) notée(s). Sous ce seuil, une alerte
                  est levée. Laissé vide, la moyenne se calcule quand même
                  mais rien ne prévient personne.
                </span>
              </div>
            </div>
          )}
          <ProgrammeSelector
            programs={programs}
            selected={programmeIds}
            onToggle={toggleProgramme}
            onClear={() => setProgrammeIds([])}
          />
          <div className="border-t border-[#EFF0F6] pt-4">
            <div className="flex items-center justify-between mb-3 sticky top-0 bg-white py-1 z-10">
              <h4 className="text-[13px] font-semibold text-[#1A1B2E]">Questions ({questions.length})</h4>
              <button data-testid="qbuilder-add-question" onClick={addQuestion} className="h-9 px-3 rounded-xl bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold flex items-center gap-1.5 hover:bg-[#EDE8FF]">
                <Plus className="w-3.5 h-3.5" /> Ajouter une question
              </button>
            </div>
            {questions.length === 0 ? (
              <div className="rounded-xl bg-[#FAFAFE] border border-dashed border-[#EFF0F6] p-5 text-center text-[12.5px] text-[#8E8FA5]">Aucune question. Cliquez sur « Ajouter une question » pour démarrer.</div>
            ) : (
              <div className="space-y-2.5">
                {questions.map((q, i) => (
                  <QuestionEditor
                    key={q.id}
                    idx={i}
                    total={questions.length}
                    question={q}
                    allQuestions={questions}
                    onUpdate={(patch) => updateQ(i, patch)}
                    onRemove={() => removeQ(i)}
                    onMove={(dir) => moveQ(i, dir)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#EFF0F6] flex items-center justify-end gap-2 shrink-0 bg-white rounded-b-2xl">
          <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB]">Annuler</button>
          <button data-testid="qbuilder-save" onClick={save} disabled={busy} className="h-10 px-5 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold hover:opacity-95 disabled:opacity-60 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function ProgrammeSelector({ programs, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = (programs || []).filter((p) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (p.title || '').toLowerCase().includes(s) || (p.id || '').toLowerCase().includes(s);
  });
  const selectedDocs = (programs || []).filter((p) => selected.includes(p.id));

  return (
    <div>
      <label className="block text-[12px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">Programmes rattachés</label>
      <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-2.5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[12.5px] text-[#4A4B63]">
            {selected.length === 0
              ? <span className="italic text-[#8E8FA5]">Aucun programme — utilisé par défaut pour toutes les sessions de cette catégorie.</span>
              : <span><strong className="text-[#4F46E5]">{selected.length}</strong> programme(s) rattaché(s)</span>
            }
          </div>
          <div className="flex items-center gap-1">
            {selected.length > 0 && (
              <button onClick={onClear} className="text-[11px] text-[#EF4444] hover:underline">Tout retirer</button>
            )}
            <button
              data-testid="qbuilder-programme-toggle"
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="h-8 px-2.5 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#4F46E5] hover:bg-[#EEF2FF] flex items-center gap-1"
            >
              {open ? 'Fermer' : 'Choisir des programmes'}
            </button>
          </div>
        </div>
        {selectedDocs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {selectedDocs.map((p) => (
              <button key={p.id} onClick={() => onToggle(p.id)} className="px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[11px] font-semibold flex items-center gap-1 hover:bg-[#EDE8FF]">
                {p.title || p.id} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}
        {open && (
          <div className="rounded-lg border border-[#EFF0F6] bg-white max-h-[200px] overflow-hidden flex flex-col">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un programme…"
              className="px-3 py-2 border-b border-[#EFF0F6] text-[12.5px] focus:outline-none"
            />
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && <div className="px-3 py-3 text-[11.5px] text-[#8E8FA5] italic">Aucun programme trouvé.</div>}
              {filtered.map((p) => {
                const isSel = selected.includes(p.id);
                return (
                  <label key={p.id} data-testid={`qbuilder-programme-${p.id}`} className="flex items-center gap-2 px-3 py-2 hover:bg-[#FAFAFE] cursor-pointer text-[12.5px]">
                    <input type="checkbox" checked={isSel} onChange={() => onToggle(p.id)} className="accent-[#4F46E5]" />
                    <span className="flex-1 truncate">{p.title || p.id}</span>
                    <span className="text-[10.5px] text-[#8E8FA5]">{p.id}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionEditor({ idx, total, question, allQuestions, onUpdate, onRemove, onMove }) {
  const [optionsText, setOptionsText] = useState((question.options || []).join('\n'));
  useEffect(() => {
    onUpdate({ options: optionsText.split('\n').map((s) => s.trim()).filter(Boolean) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsText]);

  // Earlier questions only — ref must point to a question above this one
  const eligibleRefs = (allQuestions || []).slice(0, idx).filter((q) => ['single', 'multi', 'scale', 'text'].includes(q.type));

  // Normalize show_if to the new multi-criteria shape {mode, conditions[]}
  const conditions = useMemo(() => {
    if (!question.show_if) return null;
    if (Array.isArray(question.show_if.conditions)) return question.show_if;
    if (question.show_if.question_id) {
      // Migrate legacy {question_id, equals} → {mode: AND, conditions: [{op:'equals',value}]}
      return {
        mode: 'AND',
        conditions: [{ question_id: question.show_if.question_id, op: 'equals', value: question.show_if.equals ?? '' }],
      };
    }
    return null;
  }, [question.show_if]);
  const showIfEnabled = !!conditions;

  const updateConditions = (next) => {
    onUpdate({ show_if: next ? { mode: next.mode || 'AND', conditions: next.conditions || [] } : null });
  };
  const addCondition = () => {
    const ref = eligibleRefs[0];
    if (!ref) return;
    const cur = conditions || { mode: 'AND', conditions: [] };
    updateConditions({ ...cur, conditions: [...cur.conditions, { question_id: ref.id, op: 'equals', value: '' }] });
  };
  const removeCondition = (i) => {
    if (!conditions) return;
    const next = conditions.conditions.filter((_, j) => j !== i);
    updateConditions(next.length ? { ...conditions, conditions: next } : null);
  };
  const setCondition = (i, patch) => {
    if (!conditions) return;
    const next = conditions.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c));
    updateConditions({ ...conditions, conditions: next });
  };

  // ---- Skip logic (goto_per_option), single-type only ----
  const laterRefs = (allQuestions || []).slice(idx + 1);
  const gotoEnabled = !!question.goto_per_option;
  const setGoto = (opt, target) => {
    const next = { ...(question.goto_per_option || {}) };
    if (target) next[opt] = target; else delete next[opt];
    onUpdate({ goto_per_option: Object.keys(next).length ? next : null });
  };

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5" data-testid={`qbuilder-q-${idx}`}>
      <div className="flex items-start gap-2">
        <span className="w-7 h-7 rounded-lg bg-white border border-[#EFF0F6] flex items-center justify-center text-[12px] font-bold text-[#4F46E5] shrink-0">{idx + 1}</span>
        <div className="flex-1 space-y-2">
          <input
            data-testid={`qbuilder-q-${idx}-label`}
            value={question.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            placeholder="Libellé de la question"
            className="w-full h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={question.type}
              onChange={(e) => onUpdate({ type: e.target.value })}
              data-testid={`qbuilder-q-${idx}-type`}
              className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]"
            >
              {Object.entries(QTYPE_LABEL).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-[12px] text-[#4A4B63] cursor-pointer">
              <input type="checkbox" checked={!!question.required} onChange={(e) => onUpdate({ required: e.target.checked })} className="accent-[#4F46E5]" />
              Obligatoire
            </label>
            {question.type === 'scale' && (
              <>
                <span className="text-[12px] text-[#6B6C85]">de</span>
                <input type="number" value={question.scale_min ?? 1} onChange={(e) => onUpdate({ scale_min: parseInt(e.target.value) || 1 })} className="w-14 h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px] text-center" />
                <span className="text-[12px] text-[#6B6C85]">à</span>
                <input type="number" value={question.scale_max ?? 5} onChange={(e) => onUpdate({ scale_max: parseInt(e.target.value) || 5 })} className="w-14 h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px] text-center" />
              </>
            )}
            <div className="flex-1" />
            <button onClick={() => onMove(-1)} disabled={idx === 0} className="w-7 h-7 rounded-lg hover:bg-white text-[#6B6C85] disabled:opacity-30" title="Monter">↑</button>
            <button onClick={() => onMove(1)} disabled={idx === total - 1} className="w-7 h-7 rounded-lg hover:bg-white text-[#6B6C85] disabled:opacity-30" title="Descendre">↓</button>
            <button data-testid={`qbuilder-q-${idx}-remove`} onClick={onRemove} className="w-7 h-7 rounded-lg hover:bg-white text-[#EF4444]" title="Supprimer"><X className="w-3.5 h-3.5 mx-auto" /></button>
          </div>
          {(question.type === 'single' || question.type === 'multi') && (
            <textarea
              data-testid={`qbuilder-q-${idx}-options`}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={3}
              placeholder={'Une option par ligne\nEx. Oui\nNon\nPeut-être'}
              className="w-full px-3 py-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px] focus:border-[#4F46E5] focus:outline-none resize-none font-mono"
            />
          )}
          {/* La correction. Rien n'est coché, rien n'est noté : une question
              qui ne déclare pas de bonne réponse ne compte pas. */}
          {(question.type === 'single' || question.type === 'multi')
            && (question.options || []).length > 0 && (
            <div className="rounded-lg border border-dashed border-[#A7F3E4] bg-[#F4FDFB] p-2.5"
                 data-testid={`qbuilder-q-${idx}-correction`}>
              <div className="text-[11.5px] font-semibold text-[#0F766E] mb-1.5">
                Cochez la ou les bonnes réponses — sinon la question ne compte pas
              </div>
              <div className="space-y-1">
                {(question.options || []).map((opt, oi) => {
                  const bonnes = question.bonnes_reponses || [];
                  const coche = bonnes.includes(opt);
                  return (
                    <label key={`${opt}-${oi}`} className="flex items-center gap-2 text-[12px] cursor-pointer">
                      <input
                        type="checkbox" checked={coche}
                        data-testid={`qbuilder-q-${idx}-bonne-${oi}`}
                        onChange={() => onUpdate({
                          bonnes_reponses: coche
                            ? bonnes.filter((x) => x !== opt)
                            : [...bonnes, opt],
                        })}
                        className="accent-[#0F766E]"
                      />
                      <span className={coche ? 'text-[#0F766E] font-semibold' : 'text-[#4A4B63]'}>{opt}</span>
                    </label>
                  );
                })}
              </div>
              {(question.bonnes_reponses || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-[#D7F5EE]">
                  <label className="text-[11.5px] text-[#4A4B63] flex items-center gap-1.5">
                    Points
                    <input
                      type="number" min="0" step="0.5"
                      value={question.points === undefined ? 1 : question.points}
                      onChange={(e) => onUpdate({ points: Number(e.target.value) })}
                      className="w-16 h-7 px-2 rounded-lg border border-[#EFF0F6] text-[12px]"
                    />
                  </label>
                  {question.type === 'multi' && (
                    <label className="text-[11.5px] text-[#4A4B63] flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox" checked={!!question.notation_partielle}
                        onChange={(e) => onUpdate({ notation_partielle: e.target.checked })}
                        className="accent-[#4F46E5]"
                      />
                      Notation partielle
                      <span className="text-[10.5px] text-[#8E8FA5]">
                        (sinon tout ou rien)
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
          {questionNotee(question) && (
            <label className="flex flex-wrap items-center gap-2 text-[11.5px] text-[#4A4B63] cursor-pointer">
              <input
                type="checkbox" checked={!!question.critique}
                data-testid={`qbuilder-q-${idx}-critique`}
                onChange={(e) => onUpdate({ critique: e.target.checked })}
                className="accent-[#B45309]"
              />
              Question critique
              <span className="text-[10.5px] text-[#8E8FA5]">
                — elle lève l&rsquo;alerte à elle seule si elle passe sous le
                seuil, même si la moyenne tient
              </span>
            </label>
          )}
          {(question.type === 'text' || question.type === 'long_text') && (
            <label className="flex flex-wrap items-center gap-2 text-[11.5px] text-[#4A4B63] cursor-pointer">
              <input
                type="checkbox" checked={!!question.a_corriger}
                data-testid={`qbuilder-q-${idx}-a-corriger`}
                onChange={(e) => onUpdate({ a_corriger: e.target.checked })}
                className="accent-[#4F46E5]"
              />
              À corriger à la main
              {question.a_corriger && (
                <>
                  <input
                    type="number" min="0" step="0.5"
                    value={question.points === undefined ? 1 : question.points}
                    onChange={(e) => onUpdate({ points: Number(e.target.value) })}
                    className="w-16 h-7 px-2 rounded-lg border border-[#EFF0F6] text-[12px]"
                  />
                  <span className="text-[10.5px] text-[#8E8FA5]">
                    points — tant qu&rsquo;elle n&rsquo;est pas lue, la copie
                    n&rsquo;a pas de verdict
                  </span>
                </>
              )}
            </label>
          )}
          {/* ---- Conditional display (multi-criteria) ---- */}
          {idx > 0 && (
            <div className="rounded-lg border border-dashed border-[#EFF0F6] bg-white p-2.5">
              <label className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#4A4B63] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`qbuilder-q-${idx}-show-if-toggle`}
                  checked={showIfEnabled}
                  onChange={(e) => {
                    if (!e.target.checked) updateConditions(null);
                    else {
                      const ref = eligibleRefs[0];
                      if (ref) updateConditions({ mode: 'AND', conditions: [{ question_id: ref.id, op: 'equals', value: '' }] });
                    }
                  }}
                  className="accent-[#4F46E5]"
                  disabled={eligibleRefs.length === 0}
                />
                Afficher seulement si…
                {eligibleRefs.length === 0 && <span className="text-[10.5px] text-[#8E8FA5] italic">(aucune question précédente compatible)</span>}
              </label>
              {showIfEnabled && (
                <div className="mt-2 space-y-1.5">
                  {conditions.conditions.length > 1 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10.5px] text-[#6B6C85]">Combiner avec :</span>
                      <div className="flex bg-[#F6F7FB] rounded-md p-0.5">
                        {['AND', 'OR'].map((m) => (
                          <button
                            key={m}
                            data-testid={`qbuilder-q-${idx}-show-if-mode-${m.toLowerCase()}`}
                            onClick={() => updateConditions({ ...conditions, mode: m })}
                            className={`px-2.5 h-6 text-[10.5px] font-bold rounded ${conditions.mode === m ? 'bg-white shadow-sm text-[#1A1B2E]' : 'text-[#6B6C85]'}`}
                          >
                            {m === 'AND' ? 'TOUTES (ET)' : 'AU MOINS UNE (OU)'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {conditions.conditions.map((cond, ci) => {
                    const refQ = (allQuestions || []).find((q) => q.id === cond.question_id);
                    const refOpts = refQ?.type === 'single' || refQ?.type === 'multi'
                      ? (refQ.options || [])
                      : refQ?.type === 'scale'
                        ? Array.from({ length: (refQ.scale_max ?? 5) - (refQ.scale_min ?? 1) + 1 }, (_, i) => String((refQ.scale_min ?? 1) + i))
                        : [];
                    return (
                      <div key={ci} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-1.5">
                        <select
                          data-testid={`qbuilder-q-${idx}-cond-${ci}-ref`}
                          value={cond.question_id}
                          onChange={(e) => setCondition(ci, { question_id: e.target.value, value: '' })}
                          className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                        >
                          {eligibleRefs.map((qq, i) => <option key={qq.id} value={qq.id}>{i + 1}. {(qq.label || '(sans libellé)').slice(0, 40)}</option>)}
                        </select>
                        <select
                          data-testid={`qbuilder-q-${idx}-cond-${ci}-op`}
                          value={cond.op || 'equals'}
                          onChange={(e) => setCondition(ci, { op: e.target.value })}
                          className="h-8 px-1.5 rounded-lg border border-[#EFF0F6] bg-white text-[11px]"
                        >
                          <option value="equals">est</option>
                          <option value="not_equals">n'est pas</option>
                          <option value="gte">≥</option>
                          <option value="lte">≤</option>
                          <option value="in">contient</option>
                        </select>
                        {refOpts.length > 0 ? (
                          <select
                            data-testid={`qbuilder-q-${idx}-cond-${ci}-val`}
                            value={String(cond.value ?? '')}
                            onChange={(e) => setCondition(ci, { value: refQ?.type === 'scale' ? parseInt(e.target.value) : e.target.value })}
                            className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                          >
                            <option value="">— choisir —</option>
                            {refOpts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <input
                            type="text"
                            data-testid={`qbuilder-q-${idx}-cond-${ci}-val`}
                            value={cond.value ?? ''}
                            onChange={(e) => setCondition(ci, { value: e.target.value })}
                            placeholder="Valeur"
                            className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                          />
                        )}
                        <button
                          onClick={() => removeCondition(ci)}
                          className="w-7 h-7 rounded-lg hover:bg-[#FEE2E2] text-[#EF4444] flex items-center justify-center"
                          title="Retirer cette condition"
                        ><X className="w-3 h-3" /></button>
                      </div>
                    );
                  })}
                  <button
                    data-testid={`qbuilder-q-${idx}-add-condition`}
                    onClick={addCondition}
                    className="text-[11px] text-[#4F46E5] font-semibold hover:underline"
                  >+ Ajouter une condition</button>
                </div>
              )}
            </div>
          )}

          {/* ---- Skip logic (goto_per_option) — single only ---- */}
          {question.type === 'single' && (question.options || []).length > 0 && laterRefs.length > 0 && (
            <div className="rounded-lg border border-dashed border-[#EFF0F6] bg-white p-2.5">
              <label className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[#4A4B63] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`qbuilder-q-${idx}-goto-toggle`}
                  checked={gotoEnabled}
                  onChange={(e) => onUpdate({ goto_per_option: e.target.checked ? {} : null })}
                  className="accent-[#4F46E5]"
                />
                Saut conditionnel selon la réponse
              </label>
              {gotoEnabled && (
                <div className="mt-2 space-y-1.5">
                  <div className="text-[10.5px] text-[#8E8FA5] italic">Pour chaque option, choisissez la prochaine question à afficher.</div>
                  {(question.options || []).map((opt) => (
                    <div key={opt} className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
                      <span className="text-[12px] text-[#1A1B2E] truncate">{opt}</span>
                      <span className="text-[11px] text-[#6B6C85]">→</span>
                      <select
                        data-testid={`qbuilder-q-${idx}-goto-${opt.replace(/\s+/g, '_')}`}
                        value={(question.goto_per_option || {})[opt] || ''}
                        onChange={(e) => setGoto(opt, e.target.value)}
                        className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[11.5px]"
                      >
                        <option value="">Continuer normalement</option>
                        {laterRefs.map((lr, i) => (
                          <option key={lr.id} value={lr.id}>Aller à Q{idx + 2 + i}. {(lr.label || '').slice(0, 32)}</option>
                        ))}
                        <option value="__end__">Terminer le questionnaire</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionnaireStatsModal({ data, onClose }) {
  return createPortal((
    <div className="fixed inset-0 z-[100] bg-[#1A1B2E]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="qbuilder-stats">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1A1B2E]">{data.questionnaire?.title}</h3>
            <p className="text-[12px] text-[#6B6C85]">{data.total} réponse(s) reçue(s)</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {(data.stats || []).map((s) => (
            <div key={s.id} className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5">
              <div className="text-[13px] font-semibold text-[#1A1B2E] mb-2">{s.label}</div>
              <div className="text-[11px] text-[#6B6C85] mb-2">{s.answered} réponse(s)</div>
              {s.type === 'scale' && s.avg != null && (
                <div className="space-y-1.5">
                  <div className="text-[14px] font-bold text-[#4F46E5]">Moyenne : {s.avg} / {s.max ?? '—'}</div>
                  <div className="flex gap-1 flex-wrap">
                    {Object.entries(s.distribution || {}).map(([k, v]) => (
                      <span key={k} className="px-2 py-0.5 rounded-full bg-[#EEF2FF] text-[#4F46E5] text-[11px] font-semibold">{k}: {v}</span>
                    ))}
                  </div>
                </div>
              )}
              {(s.type === 'single' || s.type === 'multi') && s.tally && (
                <div className="space-y-1">
                  {Object.entries(s.tally).map(([opt, n]) => {
                    const pct = s.answered > 0 ? Math.round((n / s.answered) * 100) : 0;
                    return (
                      <div key={opt} className="flex items-center gap-2">
                        <div className="text-[12px] text-[#4A4B63] flex-1 truncate">{opt}</div>
                        <div className="flex-1 h-2 rounded-full bg-white border border-[#EFF0F6] overflow-hidden">
                          <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[11px] font-semibold text-[#6B6C85] w-12 text-right">{n} ({pct}%)</div>
                      </div>
                    );
                  })}
                </div>
              )}
              {(s.type === 'text' || s.type === 'long_text') && (
                <div className="space-y-1">
                  {(s.samples || []).slice(0, 5).map((txt, i) => (
                    <div key={i} className="text-[12px] text-[#4A4B63] bg-white border border-[#EFF0F6] rounded-lg px-2.5 py-1.5">"{txt}"</div>
                  ))}
                  {(s.samples || []).length === 0 && <div className="text-[11.5px] text-[#8E8FA5] italic">Aucune réponse pour l'instant.</div>}
                </div>
              )}
            </div>
          ))}
          {(data.stats || []).length === 0 && (
            <div className="text-center text-[12.5px] text-[#8E8FA5] py-8">Aucune réponse pour l'instant.</div>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}


// =============================================================================
// OPCO Section — full CRUD for the 11 French OPCOs (Vague 84)
// =============================================================================
const PAYMENT_MODES = { subrogation: 'Subrogation directe à l\'OF', remboursement: 'Remboursement à l\'entreprise' };

function OpcosSection() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/opcos'); setItems(data || []); } catch (e) { console.warn("[silent]", e); }
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const newOne = () => setEditing({
    code: '', name: '', siret: '', address: '', postal_code: '', city: '',
    phone: '', email: '', website: '', contact_name: '', contact_email: '', contact_phone: '',
    submission_deadline_days: 15, portal_url: '', payment_mode: 'subrogation',
    payment_delay_days: 30, required_documents: ['devis', 'programme', 'convention', 'emargement'],
    iban: '', bic: '', idcc_codes: [], branches: [], notes: '', active: true,
  });
  const remove = async (id, code) => {
    if (!window.confirm(`Supprimer l'OPCO ${code} ? Les entreprises rattachées seront détachées.`)) return;
    try { await api.delete(`/opcos/${id}`); await refresh(); toast({ title: 'OPCO supprimé' }); }
    catch (err) { toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Suppression impossible' }); }
  };

  const filtered = items.filter((o) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (o.code || '').toLowerCase().includes(s) || (o.name || '').toLowerCase().includes(s) || (o.idcc_codes || []).some((c) => c.includes(s));
  });

  return (
    <div className="space-y-4">
      <div className="soft-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"><Building2 className="w-4 h-4" /></div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>OPCO — Opérateurs de Compétences</h3>
            <p className="text-[12px] text-[#6B6C85]">Gérez vos 11 OPCO partenaires : informations de prise en charge, contacts, IBAN, branches couvertes (IDCC).</p>
          </div>
          <button
            data-testid="opcos-seed"
            onClick={async () => {
              if (!window.confirm('Importer les 11 OPCO officiels (AFDAS, AKTO, ATLAS, CONSTRUCTYS, OCAPIAT, OPCO 2i, OPCO Mobilités, OPCO EP, OPCO Santé, OPCOMMERCE, Uniformation) ?\n\nLes OPCO déjà présents (par code) seront ignorés. Aucun écrasement.')) return;
              try {
                const { data } = await api.post('/opcos/seed');
                await refresh();
                toast({
                  title: data.inserted > 0 ? `${data.inserted} OPCO importés` : 'Aucun nouvel OPCO à importer',
                  description: data.inserted > 0
                    ? `${data.inserted}/${data.total} ajoutés. Vous pouvez maintenant les modifier ou en ajouter d'autres.`
                    : 'Vos OPCO sont déjà à jour.',
                });
              } catch (err) {
                toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Import impossible' });
              }
            }}
            className="h-9 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-1.5 hover:bg-[#EEF2FF]"
            title="Importer les 11 OPCO officiels français (idempotent)"
          >
            <Download className="w-3.5 h-3.5" /> Importer les officiels
          </button>
          <button data-testid="opcos-new" onClick={newOne} className="h-9 px-3.5 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95">
            <Plus className="w-3.5 h-3.5" /> Nouvel OPCO
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="w-3.5 h-3.5 text-[#8E8FA5] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="opcos-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par code, nom ou IDCC…"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none"
          />
        </div>
        {loading ? (
          <div className="text-center py-6 text-[12.5px] text-[#8E8FA5]">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 text-[12.5px] text-[#8E8FA5]">Aucun OPCO {search ? 'correspondant' : 'pour l\'instant'}.</div>
        ) : (
          <div className="space-y-1.5">
            {filtered.map((o) => (
              <div key={o.id} data-testid={`opco-row-${o.code}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#FAFAFE] border border-[#EFF0F6] hover:border-[#C7B7FA] transition-colors">
                <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wide whitespace-nowrap ${o.active ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#F3F4F8] text-[#8E8FA5]'}`}>{o.code}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{o.name}</div>
                  <div className="text-[11px] text-[#6B6C85] truncate">
                    {o.contact_name ? `Contact: ${o.contact_name}` : 'Aucun contact'}
                    {' · '}
                    {(o.idcc_codes || []).length} IDCC
                    {' · '}
                    {PAYMENT_MODES[o.payment_mode] || o.payment_mode}
                  </div>
                </div>
                {!o.active && <span className="text-[10.5px] text-[#EF4444] font-semibold">INACTIF</span>}
                <button data-testid={`opco-edit-${o.code}`} onClick={() => setEditing(o)} className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline">Éditer</button>
                <button data-testid={`opco-delete-${o.code}`} onClick={() => remove(o.id, o.code)} className="w-7 h-7 rounded-lg hover:bg-white text-[#EF4444] flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      {editing && <OpcoEditor initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await refresh(); }} />}
    </div>
  );
}

function OpcoEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIdccs = (text) => set('idcc_codes', text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean));
  const setBranches = (text) => set('branches', text.split('\n').map((s) => s.trim()).filter(Boolean));

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) { toast({ title: 'Code et raison sociale requis' }); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (initial.id) await api.put(`/opcos/${initial.id}`, payload);
      else await api.post('/opcos', payload);
      toast({ title: 'OPCO enregistré' });
      await onSaved();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Enregistrement impossible' });
    }
    setBusy(false);
  };

  return createPortal((
    <div className="fixed inset-0 z-[100] bg-[#1A1B2E]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="opco-editor">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1A1B2E]">{initial.id ? `Modifier ${initial.code}` : 'Nouvel OPCO'}</h3>
            <p className="text-[11.5px] text-[#6B6C85] mt-0.5">{form.name || 'Renseignez les informations ci-dessous'}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F6F7FB] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          <Section title="Identification">
            <Row label="Code OPCO" required>
              <input data-testid="opco-field-code" value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} className={inputCls} placeholder="AFDAS" />
            </Row>
            <Row label="Raison sociale" required>
              <input data-testid="opco-field-name" value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />
            </Row>
            <Row label="SIRET">
              <input value={form.siret || ''} onChange={(e) => set('siret', e.target.value)} className={inputCls} placeholder="14 chiffres" />
            </Row>
            <Row label="Statut">
              <label className="flex items-center gap-2 h-10 text-[13px] text-[#1A1B2E]">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set('active', e.target.checked)} className="accent-[#4F46E5]" />
                Actif
              </label>
            </Row>
          </Section>
          <Section title="Coordonnées siège">
            <Row label="Adresse">
              <input value={form.address || ''} onChange={(e) => set('address', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Code postal">
              <input value={form.postal_code || ''} onChange={(e) => set('postal_code', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Ville">
              <input value={form.city || ''} onChange={(e) => set('city', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Téléphone">
              <input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Email général">
              <input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Site web">
              <input value={form.website || ''} onChange={(e) => set('website', e.target.value)} className={inputCls} placeholder="https://..." />
            </Row>
          </Section>
          <Section title="Contact référent (votre interlocuteur OF)">
            <Row label="Nom">
              <input value={form.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Email">
              <input type="email" value={form.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} className={inputCls} />
            </Row>
            <Row label="Téléphone direct">
              <input value={form.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} className={inputCls} />
            </Row>
          </Section>
          <Section title="Procédure de prise en charge">
            <Row label="Délai de dépôt (jours avant formation)">
              <input type="number" value={form.submission_deadline_days ?? 15} onChange={(e) => set('submission_deadline_days', parseInt(e.target.value) || 0)} className={inputCls} />
            </Row>
            <Row label="URL portail de dépôt">
              <input value={form.portal_url || ''} onChange={(e) => set('portal_url', e.target.value)} className={inputCls} placeholder="https://..." />
            </Row>
            <Row label="Mode de remboursement">
              <select value={form.payment_mode || 'subrogation'} onChange={(e) => set('payment_mode', e.target.value)} className={inputCls}>
                <option value="subrogation">Subrogation directe à l'OF</option>
                <option value="remboursement">Remboursement à l'entreprise</option>
              </select>
            </Row>
            <Row label="Délai de paiement (jours)">
              <input type="number" value={form.payment_delay_days ?? 30} onChange={(e) => set('payment_delay_days', parseInt(e.target.value) || 0)} className={inputCls} />
            </Row>
            <Row label="Documents requis (un par ligne)" full>
              <textarea
                rows={3}
                value={(form.required_documents || []).join('\n')}
                onChange={(e) => set('required_documents', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                className={inputCls + ' resize-none font-mono'}
                placeholder={'devis\nprogramme\nconvention\nemargement'}
              />
            </Row>
          </Section>
          <Section title="Coordonnées bancaires (subrogation)">
            <Row label="IBAN">
              <input value={form.iban || ''} onChange={(e) => set('iban', e.target.value)} className={inputCls + ' font-mono text-[12px]'} placeholder="FR76 ..." />
            </Row>
            <Row label="BIC">
              <input value={form.bic || ''} onChange={(e) => set('bic', e.target.value)} className={inputCls + ' font-mono text-[12px]'} />
            </Row>
          </Section>
          <Section title="Branches couvertes">
            <Row label="Codes IDCC (séparés par retours ligne ou virgules)" full>
              <textarea
                data-testid="opco-field-idccs"
                rows={3}
                value={(form.idcc_codes || []).join('\n')}
                onChange={(e) => setIdccs(e.target.value)}
                className={inputCls + ' resize-none font-mono'}
                placeholder={'1486\n1672\n2120'}
              />
            </Row>
            <Row label="Branches (libellés, un par ligne)" full>
              <textarea
                rows={3}
                value={(form.branches || []).join('\n')}
                onChange={(e) => setBranches(e.target.value)}
                className={inputCls + ' resize-none'}
                placeholder={'Banque\nAssurance\nNotariat'}
              />
            </Row>
          </Section>
          <Section title="Notes">
            <Row label="Commentaires libres" full>
              <textarea rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} className={inputCls + ' resize-none'} />
            </Row>
          </Section>
        </div>
        <div className="px-6 py-4 border-t border-[#EFF0F6] flex items-center justify-end gap-2 shrink-0 bg-white">
          <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:bg-[#F6F7FB]">Annuler</button>
          <button data-testid="opco-save" onClick={save} disabled={busy} className="h-10 px-5 rounded-xl bg-[#4F46E5] text-white text-[13px] font-semibold hover:opacity-95 disabled:opacity-60 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

const inputCls = 'w-full h-10 px-3 rounded-xl border border-[#EFF0F6] bg-white text-[13px] focus:border-[#4F46E5] focus:outline-none transition-colors';

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4">
      <h4 className="text-[12px] font-bold text-[#4F46E5] uppercase tracking-wider mb-3">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Row({ label, required, full, children }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="block text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide mb-1.5">
        {label}{required && <span className="text-[#EF4444] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
